import React, { useState, useEffect, useRef } from 'react';
import { 
  BarChart, 
  TrendingUp, 
  Download, 
  Share2, 
  ChevronDown, 
  DollarSign, 
  ArrowUpRight, 
  Eye, 
  Zap,
  Video,
  Users,
  Target,
  PlusCircle,
  MinusCircle,
  Monitor
} from 'lucide-react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Pie } from 'react-chartjs-2';

// Chart.js'i kaydet
ChartJS.register(ArcElement, Tooltip, Legend);

export default function BrandingForecast() {
  const [activeTab, setActiveTab] = useState('mediaplan');
  
  // Performance Campaign Data - Budget Planning Only
  const rawData = [
    { platform: 'Google Ads', campaignType: 'Search', mediaType: 'Text Ads', targeting: 'High-Intent Keywords, Brand + Product Terms', cost: 162000 },
    { platform: 'Google Ads', campaignType: 'Performance Max', mediaType: 'Auto-optimized Assets', targeting: 'All Available Inventory, Smart Bidding', cost: 216000 },
    { platform: 'Google Ads', campaignType: 'GDN', mediaType: 'Display Banners', targeting: 'Affinity & Custom Intent Audiences', cost: 27000 },
    { platform: 'Meta Ads', campaignType: 'Sales', mediaType: 'Catalog + Dynamic Ads', targeting: 'Purchase Intent, Lookalike Audiences', cost: 195000 },
    { platform: 'Meta Ads', campaignType: 'Traffic', mediaType: 'Link Click Ads', targeting: 'Interest-based + Behavioral Targeting', cost: 130000 },
    { platform: 'Meta Ads', campaignType: 'Catalog', mediaType: 'Product Catalog Ads', targeting: 'Retargeting + Similar Audiences', cost: 162000 },
    { platform: 'RTB House', campaignType: 'Retargeting', mediaType: 'Dynamic Display Ads', targeting: 'Website Visitors, Abandoned Cart Users', cost: 108000 }
  ];

  // Platformları gruplandıralım
  const groupedByPlatform = {};
  rawData.forEach(item => {
    if (!groupedByPlatform[item.platform]) {
      groupedByPlatform[item.platform] = [];
    }
    groupedByPlatform[item.platform].push(item);
  });

     // Platform sıralaması - Performance Platforms
   const platformOrder = ['Google Ads', 'Meta Ads', 'RTB House'];

     // Platform logoları - Performance Platforms
   const platformLogos = {
     'Google Ads': '/google_ads-512.webp',
     'Meta Ads': '/6033716.png',
     'RTB House': '/29374191.png'
   };

  // Helper: resolve group logo (e.g., 'Meta' uses 'Meta Ads' logo key)
  const getGroupLogoSrc = (groupName) => {
    if (groupName === 'Meta') return platformLogos['Meta Ads'];
    return platformLogos[groupName];
  };

  // Hesaplanan toplamlar - Budget Only
  const totalCost = rawData.reduce((sum, item) => sum + item.cost, 0);

  // Platform bazında toplamlar - Budget Only
  const platformTotals = {};
  rawData.forEach(item => {
    if (!platformTotals[item.platform]) {
      platformTotals[item.platform] = {
        cost: 0
      };
    }
    platformTotals[item.platform].cost += item.cost;
  });

  // Platformların yüzde dağılımı
  const platformPercentages = {};
  Object.keys(platformTotals).forEach(platform => {
    platformPercentages[platform] = (platformTotals[platform].cost / totalCost) * 100;
  });

     // Her bir platformun renk kodu - Performance Platforms
   const platformColors = {
     'Google Ads': 'bg-blue-500',
     'Meta Ads': 'bg-indigo-500',
     'RTB House': 'bg-orange-500'
   };

  // Platform hücresindeki logo boyutlarını özelleştirmek için
  const getLogoClass = (platform) => {
    return "w-6 h-6 object-contain"; // Standart boyut
  };

  // Pie chart için veri hazırlama
  const pieChartData = {
    labels: platformOrder,
    datasets: [
      {
        data: platformOrder.map(platform => platformTotals[platform]?.cost || 0),
                 backgroundColor: [
           'rgb(59, 130, 246)',  // Google Ads - blue
           'rgb(99, 102, 241)', // Meta Ads - indigo
           'rgb(249, 115, 22)', // RTB House - orange
         ],
        borderWidth: 0,
      },
    ],
  };

  const pieChartOptions = {
    plugins: {
      legend: {
        position: 'right',
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 20,
          font: {
            size: 12
          }
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const value = context.raw;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `₺${value.toLocaleString()} (${percentage}%)`;
          }
        }
      }
    },
    cutout: '60%',
    responsive: true,
    maintainAspectRatio: false
  };

  // Public logos (robust path via PUBLIC_URL)
  const ASICS_LOGO = (typeof process !== 'undefined' && process.env && process.env.PUBLIC_URL ? process.env.PUBLIC_URL : '') + '/asicslogo.png';
  const WUNDER_LOGO = (typeof process !== 'undefined' && process.env && process.env.PUBLIC_URL ? process.env.PUBLIC_URL : '') + '/wunderlogo.webp';

  // Monthly Plan data (provided values)
  const monthlyPlan = [
    { month: 'February', budget: 45062, impressions: 1435698, clicks: 21099, estimatedPurchases: 101, views: 9012 },
    { month: 'March', budget: 45062, impressions: 1435698, clicks: 21099, estimatedPurchases: 101, views: 9012 },
    { month: 'April', budget: 45062, impressions: 1435698, clicks: 21099, estimatedPurchases: 101, views: 9012 },
    { month: 'May', budget: 45062, impressions: 1435698, clicks: 21099, estimatedPurchases: 101, views: 9012 },
    { month: 'June', budget: 45062, impressions: 1435698, clicks: 21099, estimatedPurchases: 101, views: 9012 },
    { month: 'July', budget: 45062, impressions: 1435698, clicks: 21099, estimatedPurchases: 101, views: 9012 },
    { month: 'August', budget: 45062, impressions: 1435698, clicks: 21099, estimatedPurchases: 101, views: 9012 },
    { month: 'September', budget: 50697, impressions: 1538383, clicks: 22606, estimatedPurchases: 109, views: 10140 },
    { month: 'October', budget: 58132, impressions: 1714946, clicks: 25201, estimatedPurchases: 121, views: 11626 },
    { month: 'November', budget: 67597, impressions: 1794799, clicks: 26374, estimatedPurchases: 126, views: 13520 },
    { month: 'December', budget: 58132, impressions: 1714946, clicks: 25201, estimatedPurchases: 121, views: 11626 }
  ];

  const monthlyTotals = monthlyPlan.reduce((acc, row) => {
    acc.budget += row.budget;
    acc.impressions += row.impressions;
    acc.clicks += row.clicks;
    acc.estimatedPurchases += row.estimatedPurchases;
    acc.views += row.views;
    return acc;
  }, { budget: 0, impressions: 0, clicks: 0, estimatedPurchases: 0, views: 0 });

  // Budget breakdown by platform and sub-channel (provided sheet)
  const breakdownMonths = ['February','March','April','May','June','July','August','September','October','November','December'];
  const breakdown = [
    {
      name: 'Google Ads',
      children: [
        { name: 'Google GDN', values: [4.506,4.506,4.506,4.506,4.506,4.506,4.506,5.070,5.813,6.760,5.813] },
        { name: 'Google PMAX', values: [13.519,13.519,13.519,13.519,13.519,13.519,13.519,15.209,17.440,20.279,17.440] },
        { name: 'Google Search', values: [4.506,4.506,4.506,4.506,4.506,4.506,4.506,5.070,5.813,6.760,5.813] }
      ]
    },
    {
      name: 'Meta',
      children: [
        { name: 'Meta Catalog Conversion', values: [11.266,11.266,11.266,11.266,11.266,11.266,11.266,12.674,14.533,16.899,14.533] },
        { name: 'Meta Traffic', values: [6.759,6.759,6.759,6.759,6.759,6.759,6.759,7.604,8.720,10.139,8.720] },
        { name: 'Meta Video', values: [4.506,4.506,4.506,4.506,4.506,4.506,4.506,5.070,5.813,6.760,5.813] }
      ]
    }
  ];

  const calcRowTotals = (values) => values.reduce((a, b) => a + b, 0);
  const calcMonthTotals = (tree) => breakdownMonths.map((_, idx) => (
    tree.reduce((sum, group) => sum + group.children.reduce((s, c) => s + c.values[idx], 0), 0)
  ));
  const breakdownMonthTotals = calcMonthTotals(breakdown);
  const breakdownGrandTotal = breakdownMonthTotals.reduce((a, b) => a + b, 0);

  // Clicks breakdown (K) per sub-channel
  const clicksBreakdown = [
    {
      name: 'Google Ads',
      children: [
        { name: 'Google GDN', values: [8.193,8.193,8.193,8.193,8.193,8.193,8.193,8.779,9.786,10.242,9.786] },
        { name: 'Google PMAX', values: [1.870,1.870,1.870,1.870,1.870,1.870,1.870,2.003,2.233,2.337,2.233] },
        { name: 'Google Search', values: [0.980,0.980,0.980,0.980,0.980,0.980,0.980,1.050,1.170,1.225,1.170] }
      ]
    },
    {
      name: 'Meta',
      children: [
        { name: 'Meta Catalog Conversion', values: [3.219,3.219,3.219,3.219,3.219,3.219,3.219,3.449,3.845,4.024,3.845] },
        { name: 'Meta Traffic', values: [6.759,6.759,6.759,6.759,6.759,6.759,6.759,7.242,8.074,8.449,8.074] },
        { name: 'Meta Video', values: [0.078,0.078,0.078,0.078,0.078,0.078,0.078,0.083,0.093,0.097,0.093] }
      ]
    }
  ];

  const clicksMonthTotals = calcMonthTotals(clicksBreakdown);
  const clicksGrandTotal = clicksMonthTotals.reduce((a, b) => a + b, 0);

  // Estimated Purchase breakdown per sub-channel
  const purchasesBreakdown = [
    {
      name: 'Google Ads',
      children: [
        { name: 'Google GDN', values: [0,0,0,0,0,0,0,0,0,0,0] },
        { name: 'Google PMAX', values: [44,44,44,44,44,44,44,47,52,55,52] },
        { name: 'Google Search', values: [14,14,14,14,14,14,14,15,17,17,17] }
      ]
    },
    {
      name: 'Meta',
      children: [
        { name: 'Meta Catalog Conversion', values: [43,43,43,43,43,43,43,47,52,54,52] },
        { name: 'Meta Traffic', values: [0,0,0,0,0,0,0,0,0,0,0] },
        { name: 'Meta Video', values: [0,0,0,0,0,0,0,0,0,0,0] }
      ]
    }
  ];

  const purchasesMonthTotals = calcMonthTotals(purchasesBreakdown);
  const purchasesGrandTotal = purchasesMonthTotals.reduce((a, b) => a + b, 0);

  // Yearly Summary computations (real numbers)
  const yearlySpendByChannel = breakdown.map(group => ({
    channel: group.name,
    total: group.children.reduce((sum, c) => sum + calcRowTotals(c.values), 0)
  }));
  const yearlySpendTotal = yearlySpendByChannel.reduce((a, b) => a + b.total, 0);

  const yearlyClicksByChannel = clicksBreakdown.map(group => ({
    channel: group.name,
    total: group.children.reduce((sum, c) => sum + calcRowTotals(c.values), 0)
  }));
  const yearlyClicksTotal = yearlyClicksByChannel.reduce((a, b) => a + b.total, 0);

  const yearlyPurchasesByChannel = purchasesBreakdown.map(group => ({
    channel: group.name,
    total: group.children.reduce((sum, c) => sum + calcRowTotals(c.values), 0)
  }));
  const yearlyPurchasesTotal = yearlyPurchasesByChannel.reduce((a, b) => a + b.total, 0);

  // Helpers for formatting
  const formatCurrencyK = (amount) => `₺${(amount / 1000).toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}K`;
  const formatCountK = (count) => `${Math.round(count / 1000).toLocaleString('tr-TR')}K`;

  return (
    <>
    <div className="p-6">
      {/* --- Third Party Tools Section (moved to top) --- */}
      <div className="mt-6 mb-12 w-full max-w-4xl mx-auto">
        <div className="rounded-3xl shadow-xl bg-gradient-to-r from-[#9A80CA] to-[#bba6e6] p-1">
          <div className="bg-white rounded-3xl p-8 md:p-12">
            <h2 className="text-2xl md:text-3xl font-extrabold text-center mb-4 tracking-tight bg-gradient-to-r from-[#9A80CA] to-[#4F8CFF] bg-clip-text text-transparent">We manage and teach all the tools you need</h2>
            <p className="text-center text-gray-600 mb-8">All your third-party tools, managed and explained for you.</p>
            <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-4 justify-items-center items-center">
              {[
                'app-store.svg',
                'google-analytics.svg',
                'adobe.svg',
                'evernote.svg',
                'pinterest.svg',
                'messenger.svg',
                'zoom.svg',
                'apple.svg',
                'tiktok (1).svg',
                'whatsapp.svg',
                'microsoft.svg',
                'Kochava-New-Horizontal-Wht-Solid.png',
                'adjust-logo-white.svg',
                'download (4).png',
                'Mailchimp-Logo-700x394.png',
                'download.svg',
                'Hotjar_Logo.png',
                'download (5).png',
              ].map((logo, idx) => (
                <div key={idx} className="bg-gray-50 rounded-xl p-2 flex items-center justify-center shadow-sm hover:shadow-md transition">
                  <img 
                    src={`/logos/${logo}`} 
                    alt={logo.replace(/\..*$/, '')} 
                    className="w-8 h-8 object-contain" 
                    style={{ maxWidth: 40, maxHeight: 40 }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* --- SWOT Analysis (moved to top) --- */}
      <div className="mt-12 mb-16 w-full max-w-6xl mx-auto">
        <div className="rounded-3xl shadow-2xl bg-gradient-to-br from-[#f8f7fc] via-[#ede7f6] to-[#e3e8ff] p-1">
          <div className="bg-white rounded-3xl p-8 md:p-12">
            <h2 className="text-4xl md:text-5xl font-extrabold text-center mb-12 tracking-tight bg-gradient-to-r from-[#9A80CA] to-[#4F8CFF] bg-clip-text text-transparent" style={{letterSpacing: '-0.03em'}}>SWOT Analysis</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {/* Strengths */}
              <div className="rounded-2xl shadow-lg bg-gradient-to-b from-[#9A80CA] to-[#bba6e6] p-0.5">
                <div className="bg-white rounded-2xl p-6 h-full flex flex-col">
                  <div className="text-lg font-bold text-white bg-gradient-to-r from-[#9A80CA] to-[#7C5DB0] rounded-xl px-4 py-2 mb-4 text-center shadow">Strengths</div>
                  <ul className="text-sm text-gray-700 space-y-3 list-disc pl-5">
                    <li>Geniş erkek giyim ürün yelpazesi (takım elbise, gömlek, aksesuar)</li>
                    <li>Vade farksız 6 ay taksit imkanı</li>
                    <li>Müşteri destek kanalları (telefon, WhatsApp, e-mail)</li>
                  </ul>
                </div>
              </div>
              {/* Weaknesses */}
              <div className="rounded-2xl shadow-lg bg-gradient-to-b from-[#bba6e6] to-[#ede7f6] p-0.5">
                <div className="bg-white rounded-2xl p-6 h-full flex flex-col">
                  <div className="text-lg font-bold text-white bg-gradient-to-r from-[#7C5DB0] to-[#9A80CA] rounded-xl px-4 py-2 mb-4 text-center shadow">Weaknesses</div>
                  <ul className="text-sm text-gray-700 space-y-3 list-disc pl-5">
                    <li>Sadece erkek giyim segmentinde faaliyet (hedef kitle sınırlı)</li>
                    <li>Yüksek kargo baremi (2000 TL üzeri, rakipler daha düşük veriyor)</li>
                    <li>Yazlık ürünlerde sınırlı seçenek (keten gömlek, şort vb.)</li>
                    <li>Yüksek sepet ortalaması, giriş seviyesi ürün eksikliği</li>
                    <li>Online deneyim zayıf, kullanıcı bilgilendirme sistemleri geliştirilmeli</li>
                    <li>Güçlü marka bilinirliği eksikliği (yerel rakipler karşısında)</li>
                    <li>Ticimax altyapısı ile sınırlı özelleştirme imkanları</li>
                    <li>Sosyal medya takipçi sayısının düşük olması</li>
                  </ul>
                </div>
              </div>
              {/* Opportunities */}
              <div className="rounded-2xl shadow-lg bg-gradient-to-b from-[#4F8CFF] to-[#bba6e6] p-0.5">
                <div className="bg-white rounded-2xl p-6 h-full flex flex-col">
                  <div className="text-lg font-bold text-white bg-gradient-to-r from-[#4F8CFF] to-[#9A80CA] rounded-xl px-4 py-2 mb-4 text-center shadow">Opportunities</div>
                  <ul className="text-sm text-gray-700 space-y-3 list-disc pl-5">
                    <li>Yaz aylarında aranması artan ürün gruplarına sahiplik (keten, polo)</li>
                    <li>Back-to-school ve iş başlama dönemi için hazırlık yapma imkanı</li>
                    <li>Düğün sezonunda takım elbise ve smokin talebindeki artış</li>
                    <li>Mezuniyet dönemlerinde formalwear kategorisinde fırsat</li>
                  </ul>
                </div>
              </div>
              {/* Threats */}
              <div className="rounded-2xl shadow-lg bg-gradient-to-b from-[#ede7f6] to-[#bba6e6] p-0.5">
                <div className="bg-white rounded-2xl p-6 h-full flex flex-col">
                  <div className="text-lg font-bold text-white bg-gradient-to-r from-[#9A80CA] to-[#4F8CFF] rounded-xl px-4 py-2 mb-4 text-center shadow">Threats</div>
                  <ul className="text-sm text-gray-700 space-y-3 list-disc pl-5">
                    <li>Koton, LC Waikiki gibi büyük markaların agresif fiyat politikaları</li>
                    <li>Rakiplerin pazar yerlerinde özellikle Trendyol'daki güçlü varlığı</li>
                    <li>Ekonomik dalgalanmalar ve TL'deki değer kaybının etkisi</li>
                    <li>Dijital reklam maliyetlerinin sürekli artış eğilimi</li>
                    <li>Fast fashion trendinin iş giyim segmentindeki etkisi</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">Performance Media Plan</h1>
          <p className="text-gray-500">Performance-driven campaign strategy for conversion optimization</p>
        </div>
        
        <div className="flex items-center gap-3">
          <img 
            src="/7780520b-c07b-43b1-b169-d7242c6496e4.webp" 
            alt="Branding Budget Plan Logo" 
            className="h-10 object-contain"
          />
        </div>
      </div>
      
      {/* Q2 Budget & KPI Forecast (Channel Mix) moved to bottom */}

      {/* Q2 Flighting Plan (Budget Phasing) moved to bottom */}

      {/* Q2 LOB Budget Split moved to bottom */}

      {/* Performance Metrics - Budget Planning */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <div className="flex justify-between items-start">
            <div>
              <span className="text-gray-500 text-sm">Total Budget</span>
              <div className="text-2xl font-bold mt-1">₺{(totalCost/1000).toFixed(0)}K</div>
            </div>
            <div className="bg-blue-100 text-blue-600 p-2 rounded-lg">
              <DollarSign className="w-5 h-5" />
            </div>
          </div>
          <div className="flex items-center mt-2 text-green-600 text-sm">
            <ArrowUpRight className="w-3 h-3 mr-1" />
            <span>Performance optimized allocation</span>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border p-4">
          <div className="flex justify-between items-start">
            <div>
              <span className="text-gray-500 text-sm">Google Ads Budget</span>
              <div className="text-2xl font-bold mt-1">₺{(platformTotals['Google Ads']?.cost/1000 || 0).toFixed(0)}K</div>
            </div>
            <div className="bg-green-100 text-green-600 p-2 rounded-lg">
              <Target className="w-5 h-5" />
            </div>
          </div>
          <div className="flex items-center mt-2 text-blue-600 text-sm">
            <span>{((platformTotals['Google Ads']?.cost || 0) / totalCost * 100).toFixed(1)}% of total budget</span>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border p-4">
          <div className="flex justify-between items-start">
            <div>
              <span className="text-gray-500 text-sm">Meta Ads Budget</span>
              <div className="text-2xl font-bold mt-1">₺{(platformTotals['Meta Ads']?.cost/1000 || 0).toFixed(0)}K</div>
            </div>
            <div className="bg-purple-100 text-purple-600 p-2 rounded-lg">
              <Users className="w-5 h-5" />
            </div>
          </div>
          <div className="flex items-center mt-2 text-blue-600 text-sm">
            <span>{((platformTotals['Meta Ads']?.cost || 0) / totalCost * 100).toFixed(1)}% of total budget</span>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border p-4">
          <div className="flex justify-between items-start">
            <div>
              <span className="text-gray-500 text-sm">RTB House Budget</span>
              <div className="text-2xl font-bold mt-1">₺{(platformTotals['RTB House']?.cost/1000 || 0).toFixed(0)}K</div>
            </div>
            <div className="bg-orange-100 text-orange-600 p-2 rounded-lg">
              <TrendingUp className="w-5 h-5" />
            </div>
          </div>
          <div className="flex items-center mt-2 text-blue-600 text-sm">
            <span>{((platformTotals['RTB House']?.cost || 0) / totalCost * 100).toFixed(1)}% of total budget</span>
          </div>
        </div>
      </div>

      {/* Sekme Menüsü */}
      <div className="flex border-b border-gray-200 mb-6">
        <button 
          className={`px-4 py-2 text-sm font-medium ${activeTab === 'overview' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button 
          className={`px-4 py-2 text-sm font-medium ${activeTab === 'mediaplan' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
          onClick={() => setActiveTab('mediaplan')}
        >
          Media Plan
        </button>
        <button 
          className={`px-4 py-2 text-sm font-medium ${activeTab === 'budget' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
          onClick={() => setActiveTab('budget')}
        >
          Budget Allocation
        </button>
      </div>

      {/* Ana Tablo Bölümü */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                 {/* Başlık Çubuğu - Renk değiştirildi */}
         <div className="bg-gradient-to-r from-[#7C7CE7] to-[#9A9AFF] p-5 text-white flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold">Media Plan</h2>
            <p className="text-sm opacity-80">Comprehensive luxury brand campaign strategy across all platforms</p>
          </div>
          {/* Desa Logo */}
          <div className="flex items-center">
            <img 
              src="/7780520b-c07b-43b1-b169-d7242c6496e4.webp" 
              alt="Performance Logo" 
              className="h-10 object-contain"
            />
          </div>
        </div>

        {/* Tablo */}
        <div className="overflow-x-auto" style={{ maxWidth: '100%' }}>
          <table className="min-w-full divide-y divide-gray-200 table-fixed">
            <thead>
              <tr className="bg-gray-50">
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '20%' }}>Platform</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '20%' }}>Campaign Type</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '20%' }}>Media Type</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '30%' }}>Targeting Strategy</th>
                <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '10%' }}>Budget (₺) / %</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {platformOrder.map(platform => {
                const items = groupedByPlatform[platform] || [];
                return items.map((item, idx) => (
                  <tr 
                    key={`${platform}-${idx}`} 
                    className={idx % 2 === 0 ? 'bg-white hover:bg-blue-50' : 'bg-gray-50 hover:bg-blue-50'}
                  >
                    {idx === 0 ? (
                      <td 
                        className="px-4 py-3 text-sm font-medium border-r border-gray-100" 
                        rowSpan={items.length}
                        style={{ verticalAlign: 'middle' }}
                      >
                        <div className="flex items-center">
                          <div className="flex items-center gap-2">
                            {/* Platform logosu */}
                            {platformLogos[platform] && (
                              <img 
                                src={platformLogos[platform]} 
                                alt={`${platform} logo`} 
                                className={getLogoClass(platform)}
                              />
                            )}
                            <span>{platform}</span>
                          </div>
                        </div>
                      </td>
                    ) : null}
                    <td className="px-4 py-3 text-sm text-gray-700">{item.campaignType}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{item.mediaType || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-700 break-words">
                      <div className="line-clamp-2 hover:line-clamp-none cursor-pointer">{item.targeting || '-'}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-medium">
                      <div className="flex flex-col items-end gap-1">
                        <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded">₺{item.cost.toLocaleString()}</span>
                        <span className="text-xs text-gray-500">({((item.cost / totalCost) * 100).toFixed(1)}%)</span>
                      </div>
                    </td>
                  </tr>
                ));
              })}
              <tr className="bg-gray-100 font-semibold">
                <td className="px-4 py-3 text-sm text-gray-900" colSpan="4">TOTAL</td>
                <td className="px-4 py-3 text-sm text-right bg-blue-100 text-blue-800 rounded-md">₺{totalCost.toLocaleString()}</td>
              </tr>
            </tbody>
          </table>
        </div>

                  {/* Tablo Altı Bilgi Bölümü */}
          <div className="p-4 bg-gray-50 border-t">
           {/* Budget Distribution by Platform bölümü kaldırıldı */}
          </div>
      </div>

      {/* Audience Overview Table */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden mt-8">
        {/* Başlık Çubuğu */}
        <div className="bg-blue-600 p-5 text-white flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold">Audience Overview</h2>
            <p className="text-sm opacity-80">Detailed audience targeting options across platforms</p>
          </div>
        </div>

        {/* Tablo */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr className="bg-gray-50">
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Platform</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Audience Type</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Targeting Options</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {/* Meta Ads */}
              <tr className="bg-white hover:bg-blue-50">
                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium border-r border-gray-100" rowSpan={2}>
                  <div className="flex items-center gap-2">
                    <img src={platformLogos['Meta Ads']} alt="Meta Ads" className="w-6 h-6 object-contain" />
                    <span>Meta Ads</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="text-sm font-medium text-gray-900">Interest</div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-500">Demographics, Interests, Behaviors</td>
              </tr>
              <tr className="bg-gray-50 hover:bg-blue-50">
                <td className="px-4 py-3">
                  <div className="text-sm font-medium text-gray-900">Lookalike</div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-500">Custom Audiences, Lookalike Audiences</td>
              </tr>

              {/* Google Ads */}
              <tr className="bg-white hover:bg-blue-50">
                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium border-r border-gray-100" rowSpan={2}>
                  <div className="flex items-center gap-2">
                    <img src={platformLogos['YouTube']} alt="YouTube" className="w-6 h-6 object-contain" />
                    <span>YouTube</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="text-sm font-medium text-gray-900">Custom Affinity</div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-500">Topics, Life Events, Custom Affinity</td>
              </tr>
              <tr className="bg-gray-50 hover:bg-blue-50">
                <td className="px-4 py-3">
                  <div className="text-sm font-medium text-gray-900">Custom Intent</div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-500">Custom Intent Keywords, Search Patterns</td>
              </tr>
              <tr className="bg-white hover:bg-blue-50">
                <td className="px-4 py-3">
                  <div className="text-sm font-medium text-gray-900">Interest</div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-500">Interest Categories, Affinity Audiences</td>
              </tr>
              <tr className="bg-gray-50 hover:bg-blue-50">
                <td className="px-4 py-3">
                  <div className="text-sm font-medium text-gray-900">Channel Placement</div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-500">Channel Targeting, Video Placements</td>
              </tr>

              {/* DV360 */}
              <tr className="bg-white hover:bg-blue-50">
                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium border-r border-gray-100" rowSpan={5}>
                  <div className="flex items-center gap-2">
                    <img src={platformLogos['DV360']} alt="DV360" className="w-6 h-6 object-contain" />
                    <span>DV360</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="text-sm font-medium text-gray-900">Custom Affinity</div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-500">Custom Affinity Audiences</td>
              </tr>
              <tr className="bg-gray-50 hover:bg-blue-50">
                <td className="px-4 py-3">
                  <div className="text-sm font-medium text-gray-900">Custom Intent</div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-500">Keyword Intent, Search Patterns</td>
              </tr>
              <tr className="bg-white hover:bg-blue-50">
                <td className="px-4 py-3">
                  <div className="text-sm font-medium text-gray-900">Interest</div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-500">Interest Categories, Behaviors</td>
              </tr>
              <tr className="bg-gray-50 hover:bg-blue-50">
                <td className="px-4 py-3">
                  <div className="text-sm font-medium text-gray-900">Category</div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-500">IAB Categories, Content Labels</td>
              </tr>
              <tr className="bg-white hover:bg-blue-50">
                <td className="px-4 py-3">
                  <div className="text-sm font-medium text-gray-900">URL Placement</div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-500">Website Placements, Apps</td>
              </tr>

              {/* TikTok */}
              <tr className="bg-white hover:bg-blue-50">
                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium border-r border-gray-100">
                  <div className="flex items-center gap-2">
                    <img src={platformLogos['Tiktok']} alt="TikTok" className="w-8 h-8 object-contain" />
                    <span>TikTok</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="text-sm font-medium text-gray-900">Interest</div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-500">Interest Categories, Behaviors</td>
              </tr>

              {/* Pinterest */}
              <tr className="bg-white hover:bg-blue-50">
                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium border-r border-gray-100">
                  <div className="flex items-center gap-2">
                    <img src={platformLogos['Pinterest']} alt="Pinterest" className="w-8 h-8 object-contain" />
                    <span>Pinterest</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="text-sm font-medium text-gray-900">Interest</div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-500">Interests, Keywords, Pin Engagement</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Bütçe Dağılımı Pie Chart */}
      <div className="grid grid-cols-2 gap-6 mb-8 mt-8">
        <div className="bg-white rounded-2xl overflow-hidden shadow-sm border">
          <div className="p-6 border-b">
            <h2 className="text-lg font-medium">Budget Distribution</h2>
            <p className="text-sm text-gray-500 mt-1">Platform-based budget allocation</p>
          </div>
          <div className="p-6">
            <div className="h-[400px] relative">
              <Pie data={pieChartData} options={pieChartOptions} />
            </div>
          </div>
        </div>

        {/* Platform Detayları */}
        <div className="bg-white rounded-2xl overflow-hidden shadow-sm border">
          <div className="p-6 border-b">
            <h2 className="text-lg font-medium">Platform Details</h2>
            <p className="text-sm text-gray-500 mt-1">Detailed budget metrics by platform</p>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {platformOrder.map((platform, index) => (
                <div key={platform} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {platform !== 'Media Buying' && (
                      <img 
                        src={platformLogos[platform]} 
                        alt={platform} 
                        className={getLogoClass(platform)}
                      />
                    )}
                    <div>
                      <div className="font-medium">{platform}</div>
                      <div className="text-sm text-gray-500">
                        Performance campaigns
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-medium">₺{platformTotals[platform].cost.toLocaleString()}</div>
                    <div className="text-sm text-gray-500">
                      {platformPercentages[platform].toFixed(1)}%
                    </div>
                  </div>
                </div>
              ))}
              {/* Total satırı */}
              <div className="flex items-center justify-between pt-4 mt-4 border-t border-gray-200">
                <div>
                  <div className="font-bold text-gray-900">TOTAL</div>
                  <div className="text-sm text-gray-500">
                    All performance campaigns
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-bold text-blue-700">₺{totalCost.toLocaleString()}</div>
                  <div className="text-sm text-gray-500">100%</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Monthly Plan Table */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden mt-8 mb-8">
        <div className="bg-gradient-to-r from-[#7C7CE7] to-[#9A9AFF] p-5 text-white flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold">Monthly Plan</h2>
            <p className="text-sm opacity-80">Provided monthly estimates</p>
          </div>
          <div className="flex items-center gap-3">
            <img
              src={ASICS_LOGO}
              alt="Asics logo"
              className="h-10 w-auto object-contain"
            />
            <img
              src={WUNDER_LOGO}
              alt="Wunder logo"
              className="h-10 w-auto object-contain"
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Month</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total Budget</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Impressions</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Clicks</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Estimated Purchases</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Views</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {monthlyPlan.map((row) => (
                <tr key={row.month} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium">{row.month}</td>
                  <td className="px-4 py-3 text-sm text-right">₺{row.budget.toLocaleString('tr-TR')}</td>
                  <td className="px-4 py-3 text-sm text-right">{row.impressions.toLocaleString('tr-TR')}</td>
                  <td className="px-4 py-3 text-sm text-right">{row.clicks.toLocaleString('tr-TR')}</td>
                  <td className="px-4 py-3 text-sm text-right">{row.estimatedPurchases.toLocaleString('tr-TR')}</td>
                  <td className="px-4 py-3 text-sm text-right">{row.views.toLocaleString('tr-TR')}</td>
                </tr>
              ))}
              <tr className="bg-gray-100 font-semibold">
                <td className="px-4 py-3 text-sm">GRAND TOTAL ({monthlyPlan.length} months)</td>
                <td className="px-4 py-3 text-sm text-right">₺{monthlyTotals.budget.toLocaleString('tr-TR')}</td>
                <td className="px-4 py-3 text-sm text-right">{monthlyTotals.impressions.toLocaleString('tr-TR')}</td>
                <td className="px-4 py-3 text-sm text-right">{monthlyTotals.clicks.toLocaleString('tr-TR')}</td>
                <td className="px-4 py-3 text-sm text-right">{monthlyTotals.estimatedPurchases.toLocaleString('tr-TR')}</td>
                <td className="px-4 py-3 text-sm text-right">{monthlyTotals.views.toLocaleString('tr-TR')}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Budget Breakdown Table */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden mt-8">
        <div className="bg-gradient-to-r from-[#9A80CA] to-[#bba6e6] p-5 text-white flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold">Budget Breakdown</h2>
            <p className="text-sm opacity-80">Platform and sub-channel monthly budgets (₺)</p>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Budget</th>
                {breakdownMonths.map((m) => (
                  <th key={m} className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{m}</th>
                ))}
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Grand Total</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {breakdown.map((group, gi) => (
                <React.Fragment key={gi}>
                  <tr className="bg-gray-50">
                    <td className="px-4 py-3 font-semibold text-gray-800">
                      <div className="flex items-center gap-2">
                        {getGroupLogoSrc(group.name) && (
                          <img
                            src={getGroupLogoSrc(group.name)}
                            alt={`${group.name} logo`}
                            className="w-5 h-5 object-contain"
                          />
                        )}
                        <span>{group.name}</span>
                      </div>
                    </td>
                    {group.children[0].values.map((_, idx) => (
                      <td key={idx} className="px-4 py-3 text-right font-semibold text-gray-800">
                        {group.children.reduce((s, c) => s + c.values[idx], 0).toLocaleString('tr-TR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
                      </td>
                    ))}
                    <td className="px-4 py-3 text-right font-semibold text-gray-800">
                      {group.children.reduce((sum, c) => sum + calcRowTotals(c.values), 0).toLocaleString('tr-TR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
                    </td>
                  </tr>
                  {group.children.map((child, ci) => (
                    <tr key={ci}>
                      <td className="px-6 py-3 text-gray-700">{child.name}</td>
                      {child.values.map((v, vi) => (
                        <td key={vi} className="px-4 py-3 text-right text-gray-700">{v.toLocaleString('tr-TR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</td>
                      ))}
                      <td className="px-4 py-3 text-right text-gray-700">{calcRowTotals(child.values).toLocaleString('tr-TR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
              <tr className="bg-gray-100 font-semibold">
                <td className="px-4 py-3">Grand Total</td>
                {breakdownMonthTotals.map((v, idx) => (
                  <td key={idx} className="px-4 py-3 text-right">{v.toLocaleString('tr-TR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</td>
                ))}
                <td className="px-4 py-3 text-right">{breakdownGrandTotal.toLocaleString('tr-TR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Clicks Breakdown Table */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden mt-8">
        <div className="bg-gradient-to-r from-[#9A80CA] to-[#bba6e6] p-5 text-white flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold">Clicks</h2>
            <p className="text-sm opacity-80">Platform and sub-channel monthly clicks (K)</p>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Clicks</th>
                {breakdownMonths.map((m) => (
                  <th key={m} className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{m}</th>
                ))}
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Grand Total</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {clicksBreakdown.map((group, gi) => (
                <React.Fragment key={gi}>
                  <tr className="bg-gray-50">
                    <td className="px-4 py-3 font-semibold text-gray-800">
                      <div className="flex items-center gap-2">
                        {getGroupLogoSrc(group.name) && (
                          <img src={getGroupLogoSrc(group.name)} alt={`${group.name} logo`} className="w-5 h-5 object-contain" />
                        )}
                        <span>{group.name}</span>
                      </div>
                    </td>
                    {group.children[0].values.map((_, idx) => (
                      <td key={idx} className="px-4 py-3 text-right font-semibold text-gray-800">
                        {group.children.reduce((s, c) => s + c.values[idx], 0).toLocaleString('tr-TR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
                      </td>
                    ))}
                    <td className="px-4 py-3 text-right font-semibold text-gray-800">
                      {group.children.reduce((sum, c) => sum + calcRowTotals(c.values), 0).toLocaleString('tr-TR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
                    </td>
                  </tr>
                  {group.children.map((child, ci) => (
                    <tr key={ci}>
                      <td className="px-6 py-3 text-gray-700">{child.name}</td>
                      {child.values.map((v, vi) => (
                        <td key={vi} className="px-4 py-3 text-right text-gray-700">{v.toLocaleString('tr-TR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</td>
                      ))}
                      <td className="px-4 py-3 text-right text-gray-700">{calcRowTotals(child.values).toLocaleString('tr-TR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
              <tr className="bg-gray-100 font-semibold">
                <td className="px-4 py-3">Grand Total</td>
                {clicksMonthTotals.map((v, idx) => (
                  <td key={idx} className="px-4 py-3 text-right">{v.toLocaleString('tr-TR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</td>
                ))}
                <td className="px-4 py-3 text-right">{clicksGrandTotal.toLocaleString('tr-TR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Estimated Purchase Breakdown Table */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden mt-8">
        <div className="bg-gradient-to-r from-[#9A80CA] to-[#bba6e6] p-5 text-white flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold">Estimated Purchases</h2>
            <p className="text-sm opacity-80">Estimated purchases per sub-channel</p>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estimated Purchases</th>
                {breakdownMonths.map((m) => (
                  <th key={m} className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{m}</th>
                ))}
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Grand Total</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {purchasesBreakdown.map((group, gi) => (
                <React.Fragment key={gi}>
                  <tr className="bg-gray-50">
                    <td className="px-4 py-3 font-semibold text-gray-800">
                      <div className="flex items-center gap-2">
                        {getGroupLogoSrc(group.name) && (
                          <img src={getGroupLogoSrc(group.name)} alt={`${group.name} logo`} className="w-5 h-5 object-contain" />
                        )}
                        <span>{group.name}</span>
                      </div>
                    </td>
                    {group.children[0].values.map((_, idx) => (
                      <td key={idx} className="px-4 py-3 text-right font-semibold text-gray-800">
                        {group.children.reduce((s, c) => s + c.values[idx], 0).toLocaleString('tr-TR')}
                      </td>
                    ))}
                    <td className="px-4 py-3 text-right font-semibold text-gray-800">
                      {group.children.reduce((sum, c) => sum + calcRowTotals(c.values), 0).toLocaleString('tr-TR')}
                    </td>
                  </tr>
                  {group.children.map((child, ci) => (
                    <tr key={ci}>
                      <td className="px-6 py-3 text-gray-700">{child.name}</td>
                      {child.values.map((v, vi) => (
                        <td key={vi} className="px-4 py-3 text-right text-gray-700">{v.toLocaleString('tr-TR')}</td>
                      ))}
                      <td className="px-4 py-3 text-right text-gray-700">{calcRowTotals(child.values).toLocaleString('tr-TR')}</td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
              <tr className="bg-gray-100 font-semibold">
                <td className="px-4 py-3">Grand Total</td>
                {purchasesMonthTotals.map((v, idx) => (
                  <td key={idx} className="px-4 py-3 text-right">{v.toLocaleString('tr-TR')}</td>
                ))}
                <td className="px-4 py-3 text-right">{purchasesGrandTotal.toLocaleString('tr-TR')}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Yearly Summary */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden mt-8">
        <div className="bg-gradient-to-r from-[#7C7CE7] to-[#9A9AFF] p-5 text-white flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold">Yearly Summary</h2>
            <p className="text-sm opacity-80">Totals and channel breakdown (real numbers)</p>
          </div>
          <div className="flex items-center gap-3">
            <img
              src={ASICS_LOGO}
              alt="Asics logo"
              className="h-10 w-auto object-contain"
            />
            <img
              src={WUNDER_LOGO}
              alt="Wunder logo"
              className="h-10 w-auto object-contain"
            />
          </div>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="rounded-xl border p-4">
            <div className="text-sm text-gray-500">Total Spend</div>
            <div className="text-2xl font-bold">{formatCurrencyK(monthlyTotals.budget)}</div>
          </div>
          <div className="rounded-xl border p-4">
            <div className="text-sm text-gray-500">Total Clicks</div>
            <div className="text-2xl font-bold">{formatCountK(monthlyTotals.clicks)}</div>
          </div>
          <div className="rounded-xl border p-4">
            <div className="text-sm text-gray-500">Estimated Purchases</div>
            <div className="text-2xl font-bold">{monthlyTotals.estimatedPurchases.toLocaleString('tr-TR')}</div>
          </div>
        </div>
        <div className="px-6 pb-6">
          <div className="overflow-x-auto rounded-xl border">
            <table className="min-w-full divide-y divide-gray-200">
              <colgroup>
                <col style={{ width: '18%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '30%' }} />
                <col style={{ width: '40%' }} />
              </colgroup>
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Channel</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Spend (₺K)</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Clicks (K)</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Estimated Purchase</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {yearlySpendByChannel.map((row) => {
                  const clicks = yearlyClicksByChannel.find(c => c.channel === row.channel)?.total || 0;
                  const purchases = yearlyPurchasesByChannel.find(p => p.channel === row.channel)?.total || 0;
                  return (
                    <tr key={row.channel}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {getGroupLogoSrc(row.channel) && (
                            <img src={getGroupLogoSrc(row.channel)} alt={`${row.channel} logo`} className="w-5 h-5 object-contain" />
                          )}
                          <span>{row.channel}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">{formatCurrencyK(row.total)}</td>
                      <td className="px-4 py-3 text-right">{clicks.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}K</td>
                      <td className="px-4 py-3 text-right">{purchases.toLocaleString('tr-TR')}</td>
                    </tr>
                  )
                })}
                <tr className="bg-gray-100 font-semibold">
                  <td className="px-4 py-3">TOTAL</td>
                  <td className="px-4 py-3 text-right">{formatCurrencyK(monthlyTotals.budget)}</td>
                  <td className="px-4 py-3 text-right">{formatCountK(monthlyTotals.clicks)}</td>
                  <td className="px-4 py-3 text-right">{monthlyTotals.estimatedPurchases.toLocaleString('tr-TR')}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Marketing Funnel Table */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden mt-8 mb-8">
        <div className="bg-gradient-to-r from-[#9A80CA] to-[#bba6e6] p-5 text-white flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold">Marketing Funnel Strategy</h2>
            <p className="text-sm opacity-80">Platform utilization across the branding journey</p>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-4 gap-6">
            {/* Awareness Stage */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-b from-blue-50 to-blue-100 transform -skew-y-6 rounded-xl"></div>
              <div className="relative p-6 bg-white rounded-xl border border-blue-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Eye className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-blue-900">Awareness</h3>
                </div>
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">Geniş kitlelere ulaşarak marka bilinirliğini artırma aşaması</p>
                  <div className="space-y-2">
                    <div className="text-sm font-medium text-gray-700">Kullanılan Formatlar:</div>
                    <ul className="text-sm text-gray-600 list-disc pl-4 space-y-1">
                      <li>Reach & Frequency</li>
                      <li>Video Reklamları</li>
                      <li>Display Reklamlar</li>
                    </ul>
                  </div>
                  <div className="pt-4 border-t">
                    <div className="text-sm font-medium text-gray-700 mb-2">Ana Platformlar</div>
                                          <div className="flex gap-2 items-center">
                        <img src={platformLogos['Meta Ads']} alt="Meta" className="w-6 h-6 object-contain" />
                        <img src={platformLogos['YouTube']} alt="YouTube" className="w-6 h-6 object-contain" />
                      </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Consideration Stage */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-b from-purple-50 to-purple-100 transform -skew-y-6 rounded-xl"></div>
              <div className="relative p-6 bg-white rounded-xl border border-purple-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Users className="w-6 h-6 text-purple-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-purple-900">Consideration</h3>
                </div>
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">Hedef kitle ile etkileşim kurma ve marka mesajını iletme aşaması</p>
                  <div className="space-y-2">
                    <div className="text-sm font-medium text-gray-700">Kullanılan Formatlar:</div>
                    <ul className="text-sm text-gray-600 list-disc pl-4 space-y-1">
                      <li>Video İçerikler</li>
                      <li>Etkileşim Kampanyaları</li>
                      <li>Sosyal Medya İçerikleri</li>
                    </ul>
                  </div>
                  <div className="pt-4 border-t">
                    <div className="text-sm font-medium text-gray-700 mb-2">Ana Platformlar</div>
                    <div className="flex gap-2 items-center">
                      <img src={platformLogos['Meta Ads']} alt="Meta" className="w-6 h-6 object-contain" />
                      <img src={platformLogos['YouTube']} alt="YouTube" className="w-6 h-6 object-contain" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Intent Stage */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-b from-green-50 to-green-100 transform -skew-y-6 rounded-xl"></div>
              <div className="relative p-6 bg-white rounded-xl border border-green-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Target className="w-6 h-6 text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-green-900">Intent</h3>
                </div>
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">Marka ile aktif etkileşim ve bilgi arama aşaması</p>
                  <div className="space-y-2">
                    <div className="text-sm font-medium text-gray-700">Kullanılan Formatlar:</div>
                    <ul className="text-sm text-gray-600 list-disc pl-4 space-y-1">
                      <li>Trafik Kampanyaları</li>
                      <li>Arama Reklamları</li>
                      <li>Alışveriş Reklamları</li>
                    </ul>
                  </div>
                  <div className="pt-4 border-t">
                                          <div className="text-sm font-medium text-gray-700 mb-2">Ana Platformlar</div>
                      <div className="flex gap-2 items-center">
                        <img src={platformLogos['YouTube']} alt="YouTube" className="w-6 h-6 object-contain" />
                        <img src={platformLogos['Pinterest']} alt="Pinterest" className="w-6 h-6 object-contain" />
                      </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Stage */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-b from-orange-50 to-orange-100 transform -skew-y-6 rounded-xl"></div>
              <div className="relative p-6 bg-white rounded-xl border border-orange-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <Monitor className="w-6 h-6 text-orange-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-orange-900">Action</h3>
                </div>
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">Marka ile doğrudan etkileşim ve satın alma aşaması</p>
                  <div className="space-y-2">
                    <div className="text-sm font-medium text-gray-700">Kullanılan Formatlar:</div>
                    <ul className="text-sm text-gray-600 list-disc pl-4 space-y-1">
                      <li>Mağaza Ziyareti</li>
                      <li>Website Dönüşümleri</li>
                      <li>Aranma Hacmi Artışı</li>
                    </ul>
                  </div>
                  <div className="pt-4 border-t">
                    <div className="text-sm font-medium text-gray-700 mb-2">Ana Platformlar</div>
                    <div className="flex gap-2 items-center">
                      <img src={platformLogos['YouTube']} alt="YouTube" className="w-6 h-6 object-contain" />
                      <img src={platformLogos['Meta Ads']} alt="Meta" className="w-6 h-6 object-contain" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Funnel Flow Indicator */}
          <div className="mt-8">
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-blue-500 via-purple-500 to-orange-500 w-full"></div>
            </div>
            <div className="flex justify-between mt-2 text-sm text-gray-500">
              <span>Geniş Kitle</span>
              <span>Hedef Kitle</span>
              <span>İlgili Kitle</span>
              <span>Potansiyel Müşteri</span>
            </div>
          </div>
        </div>
      </div>

      {/* --- First Weeks: Ajans Onboarding & Action Plan --- */}
      <div className="mt-20 mb-10">
        <h2 className="text-4xl font-extrabold mb-8 tracking-tight text-left" style={{ color: '#9A80CA', letterSpacing: '-0.03em' }}>
          First Weeks
        </h2>
        <div className="w-full max-w-5xl mx-auto">
          <div className="bg-gradient-to-br from-white via-purple-50/30 to-blue-50/30 rounded-2xl p-6 shadow-xl border border-purple-100/50">
            <table className="w-full border-separate border-spacing-y-3">
              <thead>
                <tr>
                  <th className="text-left text-sm font-bold text-gray-800 p-3 bg-gradient-to-r from-purple-100/50 to-blue-100/50 rounded-l-xl">Action</th>
                  <th className="text-left text-sm font-bold text-gray-800 p-3 bg-gradient-to-r from-purple-100/50 to-blue-100/50">Info</th>
                  <th className="text-center text-sm font-bold text-gray-800 p-3 bg-gradient-to-r from-purple-100/50 to-blue-100/50">Week 1</th>
                  <th className="text-center text-sm font-bold text-gray-800 p-3 bg-gradient-to-r from-purple-100/50 to-blue-100/50">Week 2</th>
                  <th className="text-center text-sm font-bold text-gray-800 p-3 bg-gradient-to-r from-purple-100/50 to-blue-100/50">Week 3</th>
                  <th className="text-center text-sm font-bold text-gray-800 p-3 bg-gradient-to-r from-purple-100/50 to-blue-100/50">Week 4</th>
                  <th className="text-center text-sm font-bold text-gray-800 p-3 bg-gradient-to-r from-purple-100/50 to-blue-100/50">Week 5</th>
                  <th className="text-center text-sm font-bold text-gray-800 p-3 bg-gradient-to-r from-purple-100/50 to-blue-100/50 rounded-r-xl">Week 6</th>
                </tr>
              </thead>
              <tbody>
                {/* Her satır için: action, info, haftalara göre bar */}
                {[
                  {
                    action: 'Strategy Call / Kickoff',
                    info: 'Client onboarding, goal setting, expectations',
                    weeks: [1, 0, 0, 0, 0, 0]
                  },
                  {
                    action: 'Audit',
                    info: 'Account & campaign audit',
                    weeks: [1, 1, 1, 0, 0, 0]
                  },
                  {
                    action: 'Actions',
                    info: 'Quick wins, first optimizations',
                    weeks: [1, 1, 1, 1, 0, 0]
                  },
                  {
                    action: 'Tracking Setup',
                    info: 'GA4, Meta Pixel, GTM, conversions',
                    weeks: [0, 1, 1, 0, 0, 0]
                  },
                  {
                    action: 'Competitor Analysis',
                    info: 'Market & competitor research',
                    weeks: [0, 1, 1, 0, 0, 0]
                  },
                  {
                    action: 'Reporting Setup',
                    info: 'Dashboard, automated reports',
                    weeks: [0, 0, 1, 1, 0, 0]
                  },
                  {
                    action: 'Creative Briefing',
                    info: 'Content & creative needs',
                    weeks: [0, 0, 1, 1, 0, 0]
                  },
                  {
                    action: 'Monitoring',
                    info: 'Daily checks, alerts',
                    weeks: [0, 0, 0, 1, 1, 1]
                  },
                  {
                    action: 'Settings Actions',
                    info: 'Final setup, handover',
                    weeks: [0, 0, 0, 0, 1, 1]
                  }
                ].map((row, idx) => (
                  <tr key={idx} className="group">
                    <td className="p-3 text-left font-semibold text-gray-900 align-middle w-48 bg-white/80 group-hover:bg-purple-50/50 transition-all duration-300 rounded-l-xl border-l-4 border-transparent group-hover:border-purple-300">
                      {row.action}
                    </td>
                    <td className="p-3 text-left text-gray-600 align-middle w-64 bg-white/60 group-hover:bg-purple-50/30 transition-all duration-300">
                      {row.info}
                    </td>
                    {row.weeks.map((active, wIdx) => (
                      <td key={wIdx} className="text-center align-middle p-3 bg-white/40 group-hover:bg-purple-50/20 transition-all duration-300">
                        {active ? (
                          <div className="relative">
                            <div 
                              className="h-6 rounded-full mx-auto shadow-lg transform group-hover:scale-110 transition-all duration-300" 
                              style={{ 
                                background: 'linear-gradient(135deg, #9A80CA 0%, #bba6e6 50%, #9A80CA 100%)',
                                width: '85%',
                                boxShadow: '0 4px 15px rgba(154, 128, 202, 0.3)'
                              }}
                            >
                              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
                            </div>
                          </div>
                        ) : (
                          <div className="h-6 w-4/5 mx-auto bg-gray-100 rounded-full opacity-30"></div>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Q2 Channel Monthly Breakdown */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden mt-8">
        <div className="bg-[#EA6A0A] p-5 text-white">
          <h2 className="text-xl font-bold">Q2 Channel Monthly Breakdown</h2>
          <p className="text-sm opacity-90">Budget, impressions, CPM, conversions, revenue by month and channel</p>
        </div>
        {(() => {
          const channelPlan = [
            { key: 'search', name: 'Google Search', type: 'CPC', budget: 450000, cpc: 18, ctr: 0.075, convRate: 0.022 },
            { key: 'pmax', name: 'Google Performance Max', type: 'CPC', budget: 600000, cpc: 22, ctr: 0.040, convRate: 0.020 },
            { key: 'yt', name: 'YouTube', type: 'CPM', budget: 400000, cpm: 28, ctr: 0.0035 },
            { key: 'meta_conv', name: 'Meta Conversion', type: 'CPC', budget: 350000, cpc: 14, ctr: 0.014, convRate: 0.018 },
            { key: 'meta_aw', name: 'Meta Awareness', type: 'CPM', budget: 200000, cpm: 22, ctr: 0.006 },
          ];
          const AOV = 45000; // TL - updated average order value
          const monthlyShare = { January: 0.34, February: 0.40, March: 0.26 };
          const months = ['January', 'February', 'March'];
          const formatTL = (n, fd = 0) => `₺${Number(n || 0).toLocaleString('tr-TR', { minimumFractionDigits: fd, maximumFractionDigits: fd })}`;

          // Build grouped by month with subtotals
          const monthToRows = {};
          months.forEach(m => {
            const group = [];
            channelPlan.forEach(ch => {
              const mBudget = ch.budget * monthlyShare[m];
              let impressions = 0, clicks = 0, conversions = null, revenue = null, cpa = null;
              if (ch.type === 'CPC') {
                clicks = mBudget / ch.cpc;
                impressions = ch.ctr ? (clicks / ch.ctr) : 0;
                if (ch.convRate) {
                  conversions = clicks * ch.convRate;
                  revenue = conversions * AOV;
                  cpa = conversions > 0 ? (mBudget / conversions) : null;
                }
              } else {
                impressions = (mBudget / ch.cpm) * 1000;
                clicks = ch.ctr ? (impressions * ch.ctr) : 0;
              }
              const cpm = impressions > 0 ? (mBudget / impressions) * 1000 : null;
              group.push({ channel: ch.name, budget: mBudget, impressions, clicks, conversions, revenue, cpa, cpm });
            });
            monthToRows[m] = group;
          });

          return (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Month</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Channel</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Budget</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Impr.</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">CPM</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Conv.</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Revenue</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">CPA</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {months.map((m, idx) => {
                    const group = monthToRows[m];
                    const subtotal = group.reduce((acc, r) => {
                      acc.budget += r.budget; acc.impressions += r.impressions; acc.clicks += r.clicks; acc.conversions += (r.conversions || 0); acc.revenue += (r.revenue || 0); return acc;
                    }, { budget: 0, impressions: 0, clicks: 0, conversions: 0, revenue: 0 });
                    const subtotalCpa = subtotal.conversions > 0 ? (subtotal.budget / subtotal.conversions) : null;
                    const googleRows = group.filter(r => r.channel.startsWith('Google') || r.channel === 'YouTube');
                    const metaRows = group.filter(r => r.channel.startsWith('Meta'));
                    return (
                      <React.Fragment key={idx}>
                        <tr className="bg-gray-100">
                          <td className="px-4 py-3 text-sm font-semibold" colSpan={8}>{m}</td>
                        </tr>
                        {/* Google subtotal (moved above channels) */}
                        {(() => {
                          const g = googleRows.reduce((acc, r) => { acc.budget += r.budget; acc.impressions += r.impressions; acc.clicks += r.clicks; acc.conversions += (r.conversions || 0); acc.revenue += (r.revenue || 0); return acc; }, { budget: 0, impressions: 0, clicks: 0, conversions: 0, revenue: 0 });
                          const gCpa = g.conversions > 0 ? (g.budget / g.conversions) : null;
                          const gCpm = g.impressions > 0 ? (g.budget / g.impressions) * 1000 : null;
                          return (
                            <tr className="bg-gray-50 font-medium">
                              <td className="px-4 py-3 text-sm"></td>
                              <td className="px-4 py-3 text-sm">Google Subtotal</td>
                              <td className="px-4 py-3 text-sm text-right">{formatTL(g.budget)}</td>
                              <td className="px-4 py-3 text-sm text-right">{Math.round(g.impressions).toLocaleString('tr-TR')}</td>
                              <td className="px-4 py-3 text-sm text-right">{gCpm != null ? formatTL(gCpm, 0) : '-'}</td>
                              <td className="px-4 py-3 text-sm text-right">{g.conversions ? Math.round(g.conversions).toLocaleString('tr-TR') : '-'}</td>
                              <td className="px-4 py-3 text-sm text-right">{g.revenue ? formatTL(g.revenue) : '-'}</td>
                              <td className="px-4 py-3 text-sm text-right">{gCpa ? formatTL(gCpa, 2) : '-'}</td>
                            </tr>
                          );
                        })()}
                        {/* Google channels */}
                        {googleRows.map((r, i2) => (
                          <tr key={`${m}-google-${i2}`} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm"></td>
                            <td className="px-4 py-3 text-sm">{r.channel}</td>
                            <td className="px-4 py-3 text-sm text-right">{formatTL(r.budget)}</td>
                            <td className="px-4 py-3 text-sm text-right">{Math.round(r.impressions).toLocaleString('tr-TR')}</td>
                            <td className="px-4 py-3 text-sm text-right">{r.cpm != null ? formatTL(r.cpm, 0) : '-'}</td>
                            <td className="px-4 py-3 text-sm text-right">{r.conversions != null ? Math.round(r.conversions).toLocaleString('tr-TR') : '-'}</td>
                            <td className="px-4 py-3 text-sm text-right">{r.revenue != null ? formatTL(r.revenue) : '-'}</td>
                            <td className="px-4 py-3 text-sm text-right">{r.cpa != null ? formatTL(r.cpa, 2) : '-'}</td>
                          </tr>
                        ))}
                        {/* Meta subtotal (moved above channels) */}
                        {(() => {
                          const g = metaRows.reduce((acc, r) => { acc.budget += r.budget; acc.impressions += r.impressions; acc.clicks += r.clicks; acc.conversions += (r.conversions || 0); acc.revenue += (r.revenue || 0); return acc; }, { budget: 0, impressions: 0, clicks: 0, conversions: 0, revenue: 0 });
                          const gCpa = g.conversions > 0 ? (g.budget / g.conversions) : null;
                          const gCpm = g.impressions > 0 ? (g.budget / g.impressions) * 1000 : null;
                          return (
                            <tr className="bg-gray-50 font-medium">
                              <td className="px-4 py-3 text-sm"></td>
                              <td className="px-4 py-3 text-sm">Meta Subtotal</td>
                              <td className="px-4 py-3 text-sm text-right">{formatTL(g.budget)}</td>
                              <td className="px-4 py-3 text-sm text-right">{Math.round(g.impressions).toLocaleString('tr-TR')}</td>
                              <td className="px-4 py-3 text-sm text-right">{gCpm != null ? formatTL(gCpm, 0) : '-'}</td>
                              <td className="px-4 py-3 text-sm text-right">{g.conversions ? Math.round(g.conversions).toLocaleString('tr-TR') : '-'}</td>
                              <td className="px-4 py-3 text-sm text-right">{g.revenue ? formatTL(g.revenue) : '-'}</td>
                              <td className="px-4 py-3 text-sm text-right">{gCpa ? formatTL(gCpa, 2) : '-'}</td>
                            </tr>
                          );
                        })()}
                        {/* Meta channels */}
                        {metaRows.map((r, i2) => (
                          <tr key={`${m}-meta-${i2}`} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm"></td>
                            <td className="px-4 py-3 text-sm">{r.channel}</td>
                            <td className="px-4 py-3 text-sm text-right">{formatTL(r.budget)}</td>
                            <td className="px-4 py-3 text-sm text-right">{Math.round(r.impressions).toLocaleString('tr-TR')}</td>
                            <td className="px-4 py-3 text-sm text-right">{r.cpm != null ? formatTL(r.cpm, 0) : '-'}</td>
                            <td className="px-4 py-3 text-sm text-right">{r.conversions != null ? Math.round(r.conversions).toLocaleString('tr-TR') : '-'}</td>
                            <td className="px-4 py-3 text-sm text-right">{r.revenue != null ? formatTL(r.revenue) : '-'}</td>
                            <td className="px-4 py-3 text-sm text-right">{r.cpa != null ? formatTL(r.cpa, 2) : '-'}</td>
                          </tr>
                        ))}
                        <tr className="bg-gray-50 font-semibold">
                          <td className="px-4 py-3 text-sm"></td>
                          <td className="px-4 py-3 text-sm">Subtotal</td>
                          <td className="px-4 py-3 text-sm text-right">{formatTL(subtotal.budget)}</td>
                          <td className="px-4 py-3 text-sm text-right">{Math.round(subtotal.impressions).toLocaleString('tr-TR')}</td>
                          <td className="px-4 py-3 text-sm text-right">{subtotal.impressions > 0 ? formatTL((subtotal.budget / subtotal.impressions) * 1000, 0) : '-'}</td>
                          <td className="px-4 py-3 text-sm text-right">{subtotal.conversions ? Math.round(subtotal.conversions).toLocaleString('tr-TR') : '-'}</td>
                          <td className="px-4 py-3 text-sm text-right">{subtotal.revenue ? formatTL(subtotal.revenue) : '-'}</td>
                          <td className="px-4 py-3 text-sm text-right">{subtotalCpa ? formatTL(subtotalCpa, 2) : '-'}</td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                  {/* Grand Total across Q2 */}
                  {(() => {
                    const all = Object.values(monthToRows).flat();
                    const grand = all.reduce((acc, r) => {
                      acc.budget += r.budget; acc.impressions += r.impressions; acc.clicks += r.clicks; acc.conversions += (r.conversions || 0); acc.revenue += (r.revenue || 0); return acc;
                    }, { budget: 0, impressions: 0, clicks: 0, conversions: 0, revenue: 0 });
                    const grandCpa = grand.conversions > 0 ? (grand.budget / grand.conversions) : null;
                    const grandCpm = grand.impressions > 0 ? (grand.budget / grand.impressions) * 1000 : null;
                    return (
                      <tr className="bg-gray-100 font-bold">
                        <td className="px-4 py-3 text-sm">TOTAL</td>
                        <td></td>
                        <td className="px-4 py-3 text-sm text-right">{formatTL(grand.budget)}</td>
                        <td className="px-4 py-3 text-sm text-right">{Math.round(grand.impressions).toLocaleString('tr-TR')}</td>
                        <td className="px-4 py-3 text-sm text-right">{grandCpm != null ? formatTL(grandCpm, 0) : '-'}</td>
                        <td className="px-4 py-3 text-sm text-right">{grand.conversions ? Math.round(grand.conversions).toLocaleString('tr-TR') : '-'}</td>
                        <td className="px-4 py-3 text-sm text-right">{grand.revenue ? formatTL(grand.revenue) : '-'}</td>
                        <td className="px-4 py-3 text-sm text-right">{grandCpa ? formatTL(grandCpa, 2) : '-'}</td>
                      </tr>
                    );
                  })()}
                </tbody>
              </table>
            </div>
          );
        })()}
      </div>

      {/* Q2 Product Totals (Quarter) */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden mt-8">
        <div className="bg-[#EA6A0A] p-5 text-white">
          <h2 className="text-xl font-bold">Q2 Product Totals (Quarter)</h2>
          <p className="text-sm opacity-90">Total budget and estimated revenue by product for the quarter</p>
        </div>
        {(() => {
          const AVERAGE_ORDER_VALUE = 45000; // TL - updated average order value
          // Target total spend ~ ₺2,000,000
          const channelTotal = 2000000;
          const convChannels = [
            { budget: 450000, cpc: 18, convRate: 0.022 },
            { budget: 600000, cpc: 22, convRate: 0.020 },
            { budget: 350000, cpc: 14, convRate: 0.018 },
          ];
          const totalConversions = convChannels.reduce((sum, ch) => sum + ((ch.budget / ch.cpc) * ch.convRate), 0);
          const productNames = [
            'iPhone 17',
            'iPhone 17 Pro',
            'iPhone 17 Pro Max',
            'iPhone Air',
            'Mac',
            'iPad',
            'Watch',
            'AirPods',
            'Apple TV',
            'Home Pod',
          ];
          // Heavier allocation to iPhone 17, iPhone Air, iPhone 17 Pro; solid for Mac; lower for Apple TV & Home Pod
          const productShares = {
            'iPhone 17': 0.18,
            'iPhone 17 Pro': 0.16,
            'iPhone 17 Pro Max': 0.10,
            'iPhone Air': 0.16,
            'Mac': 0.12,
            'iPad': 0.08,
            'Watch': 0.08,
            'AirPods': 0.06,
            'Apple TV': 0.03,
            'Home Pod': 0.03,
          };
          const products = productNames.map(name => ({ product: name, share: productShares[name] }));
          const formatTL = (n) => `₺${Math.round(n).toLocaleString('tr-TR')}`;

          const rows = products.map(p => {
            const budget = channelTotal * p.share;
            const conversions = totalConversions * p.share;
            const revenue = conversions * AVERAGE_ORDER_VALUE;
            return { ...p, budget, conversions, revenue };
          });

          const totals = rows.reduce((acc, r) => {
            acc.budget += r.budget; acc.conversions += r.conversions; acc.revenue += r.revenue; return acc;
          }, { budget: 0, conversions: 0, revenue: 0 });

          return (
            <div className="overflow-x-auto text-xs">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-2 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">Product</th>
                    <th className="px-4 py-2 text-right text-[11px] font-semibold text-gray-600 uppercase tracking-wider">Budget</th>
                    <th className="px-4 py-2 text-right text-[11px] font-semibold text-gray-600 uppercase tracking-wider">Est. Conversions</th>
                    <th className="px-4 py-2 text-right text-[11px] font-semibold text-gray-600 uppercase tracking-wider">Est. Revenue</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {rows.map((r, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-xs">{r.product}</td>
                      <td className="px-4 py-2 text-xs text-right">{formatTL(r.budget)}</td>
                      <td className="px-4 py-2 text-xs text-right">{Math.round(r.conversions).toLocaleString('tr-TR')}</td>
                      <td className="px-4 py-2 text-xs text-right">{formatTL(r.revenue)}</td>
                    </tr>
                  ))}
                  <tr className="bg-gray-100 font-semibold">
                    <td className="px-4 py-2 text-xs">TOTAL</td>
                    <td className="px-4 py-2 text-xs text-right">{formatTL(totals.budget)}</td>
                    <td className="px-4 py-2 text-xs text-right">{Math.round(totals.conversions).toLocaleString('tr-TR')}</td>
                    <td className="px-4 py-2 text-xs text-right">{formatTL(totals.revenue)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          );
        })()}
      </div>

      

      {/* Q2 Product Monthly P&L (Budget & Revenue) */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden mt-8">
        <div className="bg-[#EA6A0A] p-5 text-white">
          <h2 className="text-xl font-bold">Q2 Product Monthly P&L</h2>
          <p className="text-sm opacity-90">Budget and estimated revenue by month and product</p>
        </div>
        {(() => {
          const AOV = 45000; // TL - updated average order value
          const monthlyShare = { January: 0.34, February: 0.40, March: 0.26 };
          const months = ['January', 'February', 'March'];
          // Align monthly budgets with Channel table new total ₺2,000,000
          const channelTotal = 450000 + 600000 + 400000 + 350000 + 200000; // ₺2,000,000
          const convChannels = [
            { budget: 450000, cpc: 18, convRate: 0.022 },
            { budget: 600000, cpc: 22, convRate: 0.020 },
            { budget: 350000, cpc: 14, convRate: 0.018 },
          ];
          const totalConv = convChannels.reduce((sum, ch) => sum + ((ch.budget / ch.cpc) * ch.convRate), 0);
          const productNames = [
            'iPhone 17',
            'iPhone 17 Pro',
            'iPhone 17 Pro Max',
            'iPhone Air',
            'Mac',
            'iPad',
            'Watch',
            'AirPods',
            'Apple TV',
            'Home Pod',
          ];
          // Use same distribution as Q2 Product Totals
          const productShares = {
            'iPhone 17': 0.18,
            'iPhone 17 Pro': 0.16,
            'iPhone 17 Pro Max': 0.10,
            'iPhone Air': 0.16,
            'Mac': 0.12,
            'iPad': 0.08,
            'Watch': 0.08,
            'AirPods': 0.06,
            'Apple TV': 0.03,
            'Home Pod': 0.03,
          };
          const products = productNames.map(name => ({ product: name, share: productShares[name] }));
          const formatTL = (n) => `₺${Math.round(n).toLocaleString('tr-TR')}`;

          // Build grouped data by month
          const monthToRows = {};
          months.forEach(m => {
            const mBudgetTotal = channelTotal * monthlyShare[m];
            const mConversionsTotal = totalConv * monthlyShare[m];
            const group = [];
            products.forEach(p => {
              const pBudget = mBudgetTotal * p.share;
              const pConversions = mConversionsTotal * p.share;
              const pRevenue = pConversions * AOV;
              group.push({ product: p.product, budget: pBudget, conversions: pConversions, revenue: pRevenue });
            });
            monthToRows[m] = group;
          });

          return (
            <div className="overflow-x-auto text-xs">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-2 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">Month</th>
                    <th className="px-4 py-2 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">Product</th>
                    <th className="px-4 py-2 text-right text-[11px] font-semibold text-gray-600 uppercase tracking-wider">Budget</th>
                    <th className="px-4 py-2 text-right text-[11px] font-semibold text-gray-600 uppercase tracking-wider">Est. Conversions</th>
                    <th className="px-4 py-2 text-right text-[11px] font-semibold text-gray-600 uppercase tracking-wider">Est. Revenue</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {months.map((m, idx) => {
                    const group = monthToRows[m];
                    const subtotal = group.reduce((acc, r) => {
                      acc.budget += r.budget; acc.conv += r.conversions; acc.rev += r.revenue; return acc;
                    }, { budget: 0, conv: 0, rev: 0 });
                    return (
                      <React.Fragment key={idx}>
                        <tr className="bg-gray-100">
                          <td className="px-4 py-2 text-xs font-semibold" colSpan={5}>{m}</td>
                        </tr>
                        {group.map((r, i2) => (
                          <tr key={`${m}-${i2}`} className="hover:bg-gray-50">
                            <td className="px-4 py-2 text-xs"></td>
                            <td className="px-4 py-2 text-xs">{r.product}</td>
                            <td className="px-4 py-2 text-xs text-right">{formatTL(r.budget)}</td>
                            <td className="px-4 py-2 text-xs text-right">{Math.round(r.conversions).toLocaleString('tr-TR')}</td>
                            <td className="px-4 py-2 text-xs text-right">{formatTL(r.revenue)}</td>
                          </tr>
                        ))}
                        <tr className="bg-gray-50 font-semibold">
                          <td className="px-4 py-2 text-xs"></td>
                          <td className="px-4 py-2 text-xs">Subtotal</td>
                          <td className="px-4 py-2 text-xs text-right">{formatTL(subtotal.budget)}</td>
                          <td className="px-4 py-2 text-xs text-right">{Math.round(subtotal.conv).toLocaleString('tr-TR')}</td>
                          <td className="px-4 py-2 text-xs text-right">{formatTL(subtotal.rev)}</td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })()}
      </div>

      

      <style>{`
      @keyframes fadein {
        from { opacity: 0; transform: translateY(20px); }
        to { opacity: 1; transform: none; }
      }
      .animate-fadein { animation: fadein 1s cubic-bezier(.4,0,.2,1) both; }
      `}</style>

      {/* Q2 (Ocak-Şubat-Mart) Campaign Planning for Gurgencler */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden mt-12">
        <div className="bg-[#EA6A0A] p-5 text-white flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold">Gurgencler Q2 Campaign Plan (January–February–March)</h2>
            <p className="text-sm opacity-80">In brand calendar, Q2 aligns to January, February, March</p>
          </div>
        </div>

      
        {/* Ocak */}
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">January</h3>
          <div className="overflow-x-auto rounded-xl border">
            <table className="min-w-full divide-y divide-gray-200">
              <colgroup>
                <col style={{ width: '18%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '30%' }} />
                <col style={{ width: '40%' }} />
              </colgroup>
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Day</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Occasion</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notes / Campaign Opportunities</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm">1 January</td>
                  <td className="px-4 py-3 text-sm">Thursday</td>
                  <td className="px-4 py-3 text-sm">New Year's Day (Public Holiday)</td>
                  <td className="px-4 py-3 text-sm">New year discounts and "new year special" campaigns</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm">6–12 January</td>
                  <td className="px-4 py-3 text-sm">-</td>
                  <td className="px-4 py-3 text-sm">Winter Sale Period</td>
                  <td className="px-4 py-3 text-sm">End-of-season campaigns for apparel, fashion and electronics</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm">Around 15 January</td>
                  <td className="px-4 py-3 text-sm">-</td>
                  <td className="px-4 py-3 text-sm">Midterm Break (Semester) begins</td>
                  <td className="px-4 py-3 text-sm">Strong sales window for kids products, travel and entertainment</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium">31 January</td>
                  <td className="px-4 py-3 text-sm">Saturday</td>
                  <td className="px-4 py-3 text-sm">End-of-January Sale</td>
                  <td className="px-4 py-3 text-sm">Ideal timing for "last-day offers" communication</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Şubat */}
        <div className="px-6 pb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">February</h3>
          <div className="overflow-x-auto rounded-xl border">
            <table className="min-w-full divide-y divide-gray-200">
              <colgroup>
                <col style={{ width: '18%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '30%' }} />
                <col style={{ width: '40%' }} />
              </colgroup>
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Day</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Occasion</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notes / Campaign Opportunities</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm">1–13 February</td>
                  <td className="px-4 py-3 text-sm">-</td>
                  <td className="px-4 py-3 text-sm">Valentine's Day Pre-period</td>
                  <td className="px-4 py-3 text-sm">Jewelry, fashion, beauty, flowers, accessories and tech campaigns</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm">14 February</td>
                  <td className="px-4 py-3 text-sm">Saturday</td>
                  <td className="px-4 py-3 text-sm">Valentine's Day</td>
                  <td className="px-4 py-3 text-sm">One of the highest sales days in Turkish e‑commerce</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm">15–20 February</td>
                  <td className="px-4 py-3 text-sm">-</td>
                  <td className="px-4 py-3 text-sm">Winter Clearance / End of Season</td>
                  <td className="px-4 py-3 text-sm">“Get ready for spring” markdowns</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium">28 February</td>
                  <td className="px-4 py-3 text-sm">Saturday</td>
                  <td className="px-4 py-3 text-sm">Month‑end Closing Campaigns</td>
                  <td className="px-4 py-3 text-sm">Good window for “last chance” communications</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Mart */}
        <div className="px-6 pb-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">March</h3>
          <div className="overflow-x-auto rounded-xl border">
            <table className="min-w-full divide-y divide-gray-200">
              <colgroup>
                <col style={{ width: '18%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '30%' }} />
                <col style={{ width: '40%' }} />
              </colgroup>
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Day</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Occasion</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notes / Campaign Opportunities</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm">1–8 March</td>
                  <td className="px-4 py-3 text-sm">-</td>
                  <td className="px-4 py-3 text-sm">Women's Day Week (Full‑funnel Push)</td>
                  <td className="px-4 py-3 text-sm">Heavy push across gifting categories (fashion, beauty, luxury); awareness + conversion budgets increased</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm">15 March</td>
                  <td className="px-4 py-3 text-sm">Sunday</td>
                  <td className="px-4 py-3 text-sm">Spring Sale Period</td>
                  <td className="px-4 py-3 text-sm">Kick‑off for spring season sales campaigns</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Wunder x Vans Video Collaboration Plan */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden mt-12">
        <div className="bg-slate-900 p-5 text-white flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white rounded-md px-2 py-1 shadow-sm">
              <img
                src="/wunderlogo.webp"
                alt="Wunder"
                className="h-7 w-auto object-contain"
              />
            </div>
            <span className="text-xl font-semibold">×</span>
            <div className="bg-white rounded-md px-2 py-1 shadow-sm">
              <img
                src="/vanslogo2.png"
                alt="Vans"
                className="h-8 w-auto object-contain"
              />
            </div>
          </div>
          <div className="text-sm text-slate-200">
            wunder.com.tr • Total Budget: ₺200,000
          </div>
        </div>

        <div className="p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-2">Wunder x Vans Video Campaign Plan</h2>
          <p className="text-sm text-gray-600 mb-6">
            Allocation: YouTube ₺90,000 (multi-audience video) + Meta ₺110,000 (Video Views, Engagement, Reach).
          </p>

          <div className="rounded-xl border overflow-hidden">
            <div className="bg-slate-100 px-4 py-3 font-semibold text-gray-800">Campaign Plan — Pivot View</div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Platform</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Channel</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Impressions</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Reach</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Views</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Frequency</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">CPV (TRY)</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">CPM (TRY)</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Budget</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  <tr>
                    <td className="px-4 py-3 text-sm">Google Ads</td>
                    <td className="px-4 py-3 text-sm">YouTube</td>
                    <td className="px-4 py-3 text-sm">TrueView</td>
                    <td className="px-4 py-3 text-sm text-right">2,300,000</td>
                    <td className="px-4 py-3 text-sm text-right">1,150,000</td>
                    <td className="px-4 py-3 text-sm text-right">420,000</td>
                    <td className="px-4 py-3 text-sm text-right">-</td>
                    <td className="px-4 py-3 text-sm text-right">₺0.55</td>
                    <td className="px-4 py-3 text-sm text-right">-</td>
                    <td className="px-4 py-3 text-sm text-right">₺50,000</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm"></td>
                    <td className="px-4 py-3 text-sm"></td>
                    <td className="px-4 py-3 text-sm">Non‑Skippable</td>
                    <td className="px-4 py-3 text-sm text-right">1,800,000</td>
                    <td className="px-4 py-3 text-sm text-right">900,000</td>
                    <td className="px-4 py-3 text-sm text-right">-</td>
                    <td className="px-4 py-3 text-sm text-right">-</td>
                    <td className="px-4 py-3 text-sm text-right">-</td>
                    <td className="px-4 py-3 text-sm text-right">₺75.0</td>
                    <td className="px-4 py-3 text-sm text-right">₺40,000</td>
                  </tr>
                  <tr className="bg-gray-50 font-semibold">
                    <td className="px-4 py-3 text-sm">Google Total</td>
                    <td className="px-4 py-3 text-sm"></td>
                    <td className="px-4 py-3 text-sm"></td>
                    <td className="px-4 py-3 text-sm text-right">4,100,000</td>
                    <td className="px-4 py-3 text-sm text-right">2,050,000</td>
                    <td className="px-4 py-3 text-sm text-right">420,000</td>
                    <td className="px-4 py-3 text-sm text-right">-</td>
                    <td className="px-4 py-3 text-sm text-right">-</td>
                    <td className="px-4 py-3 text-sm text-right">₺48.5</td>
                    <td className="px-4 py-3 text-sm text-right">₺90,000</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm">Meta</td>
                    <td className="px-4 py-3 text-sm"></td>
                    <td className="px-4 py-3 text-sm">Video Views</td>
                    <td className="px-4 py-3 text-sm text-right">4,800,000</td>
                    <td className="px-4 py-3 text-sm text-right">2,400,000</td>
                    <td className="px-4 py-3 text-sm text-right">975,000</td>
                    <td className="px-4 py-3 text-sm text-right">-</td>
                    <td className="px-4 py-3 text-sm text-right">₺0.12</td>
                    <td className="px-4 py-3 text-sm text-right">₺23.4</td>
                    <td className="px-4 py-3 text-sm text-right">₺50,000</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm"></td>
                    <td className="px-4 py-3 text-sm"></td>
                    <td className="px-4 py-3 text-sm">Engagement</td>
                    <td className="px-4 py-3 text-sm text-right">2,850,000</td>
                    <td className="px-4 py-3 text-sm text-right">1,500,000</td>
                    <td className="px-4 py-3 text-sm text-right">-</td>
                    <td className="px-4 py-3 text-sm text-right">-</td>
                    <td className="px-4 py-3 text-sm text-right">-</td>
                    <td className="px-4 py-3 text-sm text-right">₺27.6</td>
                    <td className="px-4 py-3 text-sm text-right">₺35,000</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm"></td>
                    <td className="px-4 py-3 text-sm"></td>
                    <td className="px-4 py-3 text-sm">Reach</td>
                    <td className="px-4 py-3 text-sm text-right">3,900,000</td>
                    <td className="px-4 py-3 text-sm text-right">3,000,000</td>
                    <td className="px-4 py-3 text-sm text-right">-</td>
                    <td className="px-4 py-3 text-sm text-right">1.3</td>
                    <td className="px-4 py-3 text-sm text-right">-</td>
                    <td className="px-4 py-3 text-sm text-right">₺14.4</td>
                    <td className="px-4 py-3 text-sm text-right">₺25,000</td>
                  </tr>
                  <tr className="bg-gray-50 font-semibold">
                    <td className="px-4 py-3 text-sm">Meta Total</td>
                    <td className="px-4 py-3 text-sm"></td>
                    <td className="px-4 py-3 text-sm"></td>
                    <td className="px-4 py-3 text-sm text-right">11,550,000</td>
                    <td className="px-4 py-3 text-sm text-right">6,900,000</td>
                    <td className="px-4 py-3 text-sm text-right">975,000</td>
                    <td className="px-4 py-3 text-sm text-right">-</td>
                    <td className="px-4 py-3 text-sm text-right">-</td>
                    <td className="px-4 py-3 text-sm text-right">₺21.5</td>
                    <td className="px-4 py-3 text-sm text-right">₺110,000</td>
                  </tr>
                  <tr className="bg-gray-100 font-semibold">
                    <td className="px-4 py-3 text-sm">Grand Total</td>
                    <td className="px-4 py-3 text-sm"></td>
                    <td className="px-4 py-3 text-sm"></td>
                    <td className="px-4 py-3 text-sm text-right">15,650,000</td>
                    <td className="px-4 py-3 text-sm text-right">8,950,000</td>
                    <td className="px-4 py-3 text-sm text-right">1,395,000</td>
                    <td className="px-4 py-3 text-sm text-right">-</td>
                    <td className="px-4 py-3 text-sm text-right">-</td>
                    <td className="px-4 py-3 text-sm text-right">₺20.4</td>
                    <td className="px-4 py-3 text-sm text-right">₺200,000</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Wunder — Adidas x BAPE Performance Campaign Plan — ₺100K */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden mt-12">
        <div className="bg-gradient-to-r from-[#1a1a1a] via-[#2d2d2d] to-[#1a1a1a] p-5 text-white flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <div className="bg-white rounded-md px-2 py-1 shadow-sm">
              <img
                src="/wunderlogo.webp"
                alt="Wunder"
                className="h-7 w-auto object-contain"
              />
            </div>
            <div className="flex items-center gap-2.5">
              <div className="bg-white rounded-lg px-3 py-1.5 shadow-md flex items-center gap-2">
                <img
                  src="/Adidas_logo.png"
                  alt="Adidas"
                  className="h-6 w-auto object-contain"
                />
                <span className="text-gray-800 font-bold text-base">×</span>
                <span className="text-gray-900 font-extrabold text-sm tracking-wider" style={{ fontFamily: "'Arial Black', 'Helvetica Neue', sans-serif", letterSpacing: '0.15em' }}>BAPE</span>
              </div>
            </div>
            <div className="ml-1">
              <h2 className="text-lg font-bold">Performance Campaign Plan</h2>
              <p className="text-sm text-slate-400">Google Ads (Search + PMAX) & Meta (Sales + Reach)</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center px-3 py-1.5 bg-emerald-500 text-white text-sm font-semibold rounded-full shadow-lg shadow-emerald-500/30">
              Total Budget: ₺100,000
            </span>
          </div>
        </div>

        <div className="p-6">
          {/* Budget Split Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
              <div className="flex items-center gap-2 mb-1">
                <img src="/google_ads-512.webp" alt="Google" className="w-5 h-5 object-contain" />
                <span className="text-xs font-medium text-blue-700">Google Ads</span>
              </div>
              <p className="text-xl font-bold text-blue-800">₺25,000</p>
              <p className="text-[10px] text-blue-600 mt-0.5">25% of total • Search + PMAX</p>
            </div>
            <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-4">
              <div className="flex items-center gap-2 mb-1">
                <img src="/6033716.png" alt="Meta" className="w-5 h-5 object-contain" />
                <span className="text-xs font-medium text-indigo-700">Meta Ads</span>
              </div>
              <p className="text-xl font-bold text-indigo-800">₺75,000</p>
              <p className="text-[10px] text-indigo-600 mt-0.5">75% of total • Sales + Reach</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-medium text-gray-700">Total Clicks</span>
              </div>
              <p className="text-xl font-bold text-gray-800">12.3K</p>
              <p className="text-[10px] text-gray-600 mt-0.5">Google 3.1K + Meta 9.2K</p>
            </div>
            <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-4">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-medium text-emerald-700">Meta Reach</span>
              </div>
              <p className="text-xl font-bold text-emerald-800">1.96M</p>
              <p className="text-[10px] text-emerald-600 mt-0.5">Sales + Reach campaigns</p>
            </div>
          </div>

          {/* Campaign Table */}
          <div className="rounded-xl border overflow-hidden">
            <div className="bg-slate-100 px-4 py-3 font-semibold text-gray-800">Campaign Plan — Performance Focus</div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Platform</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campaign Type</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Impressions</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Reach</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Clicks</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">CPC (TRY)</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">CPM (TRY)</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Budget</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {/* Google Ads - PMAX */}
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium" rowSpan={2}>
                      <div className="flex items-center gap-2">
                        <img src="/google_ads-512.webp" alt="Google Ads" className="w-5 h-5 object-contain" />
                        <span>Google Ads</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">Performance Max</td>
                    <td className="px-4 py-3 text-sm text-right">-</td>
                    <td className="px-4 py-3 text-sm text-right">-</td>
                    <td className="px-4 py-3 text-sm text-right">2,080</td>
                    <td className="px-4 py-3 text-sm text-right">₺7.2</td>
                    <td className="px-4 py-3 text-sm text-right">-</td>
                    <td className="px-4 py-3 text-sm text-right font-medium">₺15,000</td>
                  </tr>
                  {/* Google Ads - Search */}
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">Search</td>
                    <td className="px-4 py-3 text-sm text-right">-</td>
                    <td className="px-4 py-3 text-sm text-right">-</td>
                    <td className="px-4 py-3 text-sm text-right">1,045</td>
                    <td className="px-4 py-3 text-sm text-right">₺9.6</td>
                    <td className="px-4 py-3 text-sm text-right">-</td>
                    <td className="px-4 py-3 text-sm text-right font-medium">₺10,000</td>
                  </tr>
                  {/* Google Subtotal */}
                  <tr className="bg-blue-50 font-semibold">
                    <td className="px-4 py-3 text-sm text-blue-800">Google Total</td>
                    <td className="px-4 py-3 text-sm"></td>
                    <td className="px-4 py-3 text-sm text-right text-blue-800">-</td>
                    <td className="px-4 py-3 text-sm text-right text-blue-800">-</td>
                    <td className="px-4 py-3 text-sm text-right text-blue-800">3,125</td>
                    <td className="px-4 py-3 text-sm text-right text-blue-800">₺8.0</td>
                    <td className="px-4 py-3 text-sm text-right text-blue-800">-</td>
                    <td className="px-4 py-3 text-sm text-right text-blue-800">₺25,000</td>
                  </tr>
                  {/* Meta - Sales */}
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium" rowSpan={2}>
                      <div className="flex items-center gap-2">
                        <img src="/6033716.png" alt="Meta" className="w-5 h-5 object-contain" />
                        <span>Meta Ads</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">Sales (Advantage+)</td>
                    <td className="px-4 py-3 text-sm text-right">920,000</td>
                    <td className="px-4 py-3 text-sm text-right">460,000</td>
                    <td className="px-4 py-3 text-sm text-right">9,200</td>
                    <td className="px-4 py-3 text-sm text-right">₺5.4</td>
                    <td className="px-4 py-3 text-sm text-right">₺54.3</td>
                    <td className="px-4 py-3 text-sm text-right font-medium">₺50,000</td>
                  </tr>
                  {/* Meta - Reach */}
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">Reach</td>
                    <td className="px-4 py-3 text-sm text-right">2,200,000</td>
                    <td className="px-4 py-3 text-sm text-right">1,500,000</td>
                    <td className="px-4 py-3 text-sm text-right">-</td>
                    <td className="px-4 py-3 text-sm text-right">-</td>
                    <td className="px-4 py-3 text-sm text-right">₺11.4</td>
                    <td className="px-4 py-3 text-sm text-right font-medium">₺25,000</td>
                  </tr>
                  {/* Meta Subtotal */}
                  <tr className="bg-indigo-50 font-semibold">
                    <td className="px-4 py-3 text-sm text-indigo-800">Meta Total</td>
                    <td className="px-4 py-3 text-sm"></td>
                    <td className="px-4 py-3 text-sm text-right text-indigo-800">3,120,000</td>
                    <td className="px-4 py-3 text-sm text-right text-indigo-800">1,960,000</td>
                    <td className="px-4 py-3 text-sm text-right text-indigo-800">9,200</td>
                    <td className="px-4 py-3 text-sm text-right text-indigo-800">₺5.4</td>
                    <td className="px-4 py-3 text-sm text-right text-indigo-800">₺24.0</td>
                    <td className="px-4 py-3 text-sm text-right text-indigo-800">₺75,000</td>
                  </tr>
                  {/* Grand Total */}
                  <tr className="bg-gray-800 text-white font-bold">
                    <td className="px-4 py-3 text-sm">Grand Total</td>
                    <td className="px-4 py-3 text-sm"></td>
                    <td className="px-4 py-3 text-sm text-right">3,120,000</td>
                    <td className="px-4 py-3 text-sm text-right">1,960,000</td>
                    <td className="px-4 py-3 text-sm text-right">12,325</td>
                    <td className="px-4 py-3 text-sm text-right">-</td>
                    <td className="px-4 py-3 text-sm text-right">₺24.0</td>
                    <td className="px-4 py-3 text-sm text-right">₺100,000</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="mt-5 p-4 rounded-xl bg-amber-50 border border-amber-200">
            <div className="flex items-start gap-2.5">
              <div className="flex-shrink-0 mt-0.5">
                <svg className="w-4 h-4 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <p className="text-xs font-semibold text-amber-800">Estimated Performance Projections</p>
                <p className="text-[11px] text-amber-700 mt-1 leading-relaxed">
                  This plan is built on estimated projections based on current market benchmarks, historical platform performance data, and industry averages. 
                  Actual results may vary depending on auction dynamics, audience behavior, seasonality, creative performance, and market conditions. 
                  All figures — including impressions, reach, clicks, CPC, and CPM — are forecasts and should be treated as directional guidance, not guaranteed outcomes.
                </p>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Wunder — Adidas Always On Campaign Plan — ₺150K/month */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden mt-12">
        <div className="bg-gradient-to-r from-[#003087] via-[#004db3] to-[#003087] p-5 text-white flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <div className="bg-white rounded-md px-2 py-1 shadow-sm">
              <img src="/wunderlogo.webp" alt="Wunder" className="h-7 w-auto object-contain" />
            </div>
            <div className="flex items-center gap-2.5">
              <div className="bg-white rounded-lg px-3 py-1.5 shadow-md flex items-center gap-2">
                <img src="/Adidas_logo.png" alt="Adidas" className="h-6 w-auto object-contain" />
              </div>
            </div>
            <div className="ml-1">
              <h2 className="text-lg font-bold">Always On Campaign Plan</h2>
              <p className="text-sm text-blue-200">Google Ads (Search + PMAX + Demand Gen) & Meta (Sales + Reach + Traffic)</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center px-3 py-1 bg-white/20 text-white text-xs font-medium rounded-full border border-white/30">
              Feb – Jun 2026
            </span>
            <span className="inline-flex items-center px-3 py-1.5 bg-emerald-500 text-white text-sm font-semibold rounded-full shadow-lg shadow-emerald-500/30">
              ₺150,000 / ay
            </span>
          </div>
        </div>

        <div className="p-6">
          {/* Budget Split Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
              <div className="flex items-center gap-2 mb-1">
                <img src="/google_ads-512.webp" alt="Google" className="w-5 h-5 object-contain" />
                <span className="text-xs font-medium text-blue-700">Google Ads</span>
              </div>
              <p className="text-xl font-bold text-blue-800">₺45,000</p>
              <p className="text-[10px] text-blue-600 mt-0.5">30% of total • Search + PMAX + Demand Gen</p>
            </div>
            <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-4">
              <div className="flex items-center gap-2 mb-1">
                <img src="/6033716.png" alt="Meta" className="w-5 h-5 object-contain" />
                <span className="text-xs font-medium text-indigo-700">Meta Ads</span>
              </div>
              <p className="text-xl font-bold text-indigo-800">₺105,000</p>
              <p className="text-[10px] text-indigo-600 mt-0.5">70% of total • Sales + Reach + Traffic</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-medium text-gray-700">Monthly Clicks</span>
              </div>
              <p className="text-xl font-bold text-gray-800">22.4K</p>
              <p className="text-[10px] text-gray-600 mt-0.5">Google 5.6K + Meta 16.8K</p>
            </div>
            <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-4">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-medium text-emerald-700">Monthly Reach</span>
              </div>
              <p className="text-xl font-bold text-emerald-800">3.57M</p>
              <p className="text-[10px] text-emerald-600 mt-0.5">Sales + Reach + Traffic campaigns</p>
            </div>
          </div>

          {/* Campaign Plan Table */}
          <div className="rounded-xl border overflow-hidden mb-8">
            <div className="bg-slate-100 px-4 py-3 font-semibold text-gray-800">Campaign Plan — Monthly Breakdown by Channel</div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Platform</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campaign Type</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Impressions</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Reach</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Clicks</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">CPC (TRY)</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">CPM (TRY)</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Budget</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {/* Google Ads - Search */}
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium" rowSpan={3}>
                      <div className="flex items-center gap-2">
                        <img src="/google_ads-512.webp" alt="Google Ads" className="w-5 h-5 object-contain" />
                        <span>Google Ads</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">Search</td>
                    <td className="px-4 py-3 text-sm text-right">-</td>
                    <td className="px-4 py-3 text-sm text-right">-</td>
                    <td className="px-4 py-3 text-sm text-right">1,500</td>
                    <td className="px-4 py-3 text-sm text-right">₺10.0</td>
                    <td className="px-4 py-3 text-sm text-right">-</td>
                    <td className="px-4 py-3 text-sm text-right font-medium">₺15,000</td>
                  </tr>
                  {/* Google Ads - PMAX */}
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">Performance Max</td>
                    <td className="px-4 py-3 text-sm text-right">-</td>
                    <td className="px-4 py-3 text-sm text-right">-</td>
                    <td className="px-4 py-3 text-sm text-right">2,860</td>
                    <td className="px-4 py-3 text-sm text-right">₺7.0</td>
                    <td className="px-4 py-3 text-sm text-right">-</td>
                    <td className="px-4 py-3 text-sm text-right font-medium">₺20,000</td>
                  </tr>
                  {/* Google Ads - Demand Gen */}
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">Demand Gen</td>
                    <td className="px-4 py-3 text-sm text-right">500,000</td>
                    <td className="px-4 py-3 text-sm text-right">350,000</td>
                    <td className="px-4 py-3 text-sm text-right">1,250</td>
                    <td className="px-4 py-3 text-sm text-right">₺8.0</td>
                    <td className="px-4 py-3 text-sm text-right">₺20.0</td>
                    <td className="px-4 py-3 text-sm text-right font-medium">₺10,000</td>
                  </tr>
                  {/* Google Subtotal */}
                  <tr className="bg-blue-50 font-semibold">
                    <td className="px-4 py-3 text-sm text-blue-800">Google Total</td>
                    <td className="px-4 py-3 text-sm"></td>
                    <td className="px-4 py-3 text-sm text-right text-blue-800">500,000</td>
                    <td className="px-4 py-3 text-sm text-right text-blue-800">350,000</td>
                    <td className="px-4 py-3 text-sm text-right text-blue-800">5,610</td>
                    <td className="px-4 py-3 text-sm text-right text-blue-800">₺8.0</td>
                    <td className="px-4 py-3 text-sm text-right text-blue-800">-</td>
                    <td className="px-4 py-3 text-sm text-right text-blue-800">₺45,000</td>
                  </tr>
                  {/* Meta - Sales */}
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium" rowSpan={3}>
                      <div className="flex items-center gap-2">
                        <img src="/6033716.png" alt="Meta" className="w-5 h-5 object-contain" />
                        <span>Meta Ads</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">Sales (Advantage+)</td>
                    <td className="px-4 py-3 text-sm text-right">730,000</td>
                    <td className="px-4 py-3 text-sm text-right">365,000</td>
                    <td className="px-4 py-3 text-sm text-right">7,400</td>
                    <td className="px-4 py-3 text-sm text-right">₺5.4</td>
                    <td className="px-4 py-3 text-sm text-right">-</td>
                    <td className="px-4 py-3 text-sm text-right font-medium">₺40,000</td>
                  </tr>
                  {/* Meta - Reach */}
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">Reach</td>
                    <td className="px-4 py-3 text-sm text-right">2,920,000</td>
                    <td className="px-4 py-3 text-sm text-right">2,100,000</td>
                    <td className="px-4 py-3 text-sm text-right">-</td>
                    <td className="px-4 py-3 text-sm text-right">-</td>
                    <td className="px-4 py-3 text-sm text-right">₺12.0</td>
                    <td className="px-4 py-3 text-sm text-right font-medium">₺35,000</td>
                  </tr>
                  {/* Meta - Traffic */}
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">Traffic</td>
                    <td className="px-4 py-3 text-sm text-right">1,500,000</td>
                    <td className="px-4 py-3 text-sm text-right">750,000</td>
                    <td className="px-4 py-3 text-sm text-right">9,375</td>
                    <td className="px-4 py-3 text-sm text-right">₺3.2</td>
                    <td className="px-4 py-3 text-sm text-right">-</td>
                    <td className="px-4 py-3 text-sm text-right font-medium">₺30,000</td>
                  </tr>
                  {/* Meta Subtotal */}
                  <tr className="bg-indigo-50 font-semibold">
                    <td className="px-4 py-3 text-sm text-indigo-800">Meta Total</td>
                    <td className="px-4 py-3 text-sm"></td>
                    <td className="px-4 py-3 text-sm text-right text-indigo-800">5,150,000</td>
                    <td className="px-4 py-3 text-sm text-right text-indigo-800">3,215,000</td>
                    <td className="px-4 py-3 text-sm text-right text-indigo-800">16,775</td>
                    <td className="px-4 py-3 text-sm text-right text-indigo-800">₺4.2</td>
                    <td className="px-4 py-3 text-sm text-right text-indigo-800">₺20.4</td>
                    <td className="px-4 py-3 text-sm text-right text-indigo-800">₺105,000</td>
                  </tr>
                  {/* Grand Total */}
                  <tr className="bg-gray-800 text-white font-bold">
                    <td className="px-4 py-3 text-sm">Grand Total</td>
                    <td className="px-4 py-3 text-sm"></td>
                    <td className="px-4 py-3 text-sm text-right">5,650,000</td>
                    <td className="px-4 py-3 text-sm text-right">3,565,000</td>
                    <td className="px-4 py-3 text-sm text-right">22,385</td>
                    <td className="px-4 py-3 text-sm text-right">-</td>
                    <td className="px-4 py-3 text-sm text-right">₺26.5</td>
                    <td className="px-4 py-3 text-sm text-right">₺150,000</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Monthly Breakdown Table */}
          <div className="rounded-xl border overflow-hidden mb-6">
            <div className="bg-slate-100 px-4 py-3 font-semibold text-gray-800">Monthly Breakdown — Always On (Feb – Jun 2026)</div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Month</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Budget</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Google Ads</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Meta Ads</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Impressions</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Reach</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Clicks</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {/* February — ramp-up, winter tail */}
                  <tr className="bg-white hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">February</td>
                    <td className="px-4 py-3 text-sm text-right font-medium">₺150,000</td>
                    <td className="px-4 py-3 text-sm text-right">₺45,000</td>
                    <td className="px-4 py-3 text-sm text-right">₺105,000</td>
                    <td className="px-4 py-3 text-sm text-right">5,120,000</td>
                    <td className="px-4 py-3 text-sm text-right">3,240,000</td>
                    <td className="px-4 py-3 text-sm text-right">20,450</td>
                  </tr>
                  {/* March — spring pickup */}
                  <tr className="bg-gray-50 hover:bg-gray-100">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">March</td>
                    <td className="px-4 py-3 text-sm text-right font-medium">₺150,000</td>
                    <td className="px-4 py-3 text-sm text-right">₺45,000</td>
                    <td className="px-4 py-3 text-sm text-right">₺105,000</td>
                    <td className="px-4 py-3 text-sm text-right">5,480,000</td>
                    <td className="px-4 py-3 text-sm text-right">3,460,000</td>
                    <td className="px-4 py-3 text-sm text-right">21,700</td>
                  </tr>
                  {/* April — momentum builds */}
                  <tr className="bg-white hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">April</td>
                    <td className="px-4 py-3 text-sm text-right font-medium">₺150,000</td>
                    <td className="px-4 py-3 text-sm text-right">₺45,000</td>
                    <td className="px-4 py-3 text-sm text-right">₺105,000</td>
                    <td className="px-4 py-3 text-sm text-right">5,780,000</td>
                    <td className="px-4 py-3 text-sm text-right">3,620,000</td>
                    <td className="px-4 py-3 text-sm text-right">22,900</td>
                  </tr>
                  {/* May — peak spring performance */}
                  <tr className="bg-gray-50 hover:bg-gray-100">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">May</td>
                    <td className="px-4 py-3 text-sm text-right font-medium">₺150,000</td>
                    <td className="px-4 py-3 text-sm text-right">₺45,000</td>
                    <td className="px-4 py-3 text-sm text-right">₺105,000</td>
                    <td className="px-4 py-3 text-sm text-right">6,050,000</td>
                    <td className="px-4 py-3 text-sm text-right">3,780,000</td>
                    <td className="px-4 py-3 text-sm text-right">23,850</td>
                  </tr>
                  {/* June — early summer, slight seasonal dip */}
                  <tr className="bg-white hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">June</td>
                    <td className="px-4 py-3 text-sm text-right font-medium">₺150,000</td>
                    <td className="px-4 py-3 text-sm text-right">₺45,000</td>
                    <td className="px-4 py-3 text-sm text-right">₺105,000</td>
                    <td className="px-4 py-3 text-sm text-right">5,820,000</td>
                    <td className="px-4 py-3 text-sm text-right">3,650,000</td>
                    <td className="px-4 py-3 text-sm text-right">22,700</td>
                  </tr>
                  {/* Total */}
                  <tr className="bg-gray-800 text-white font-bold">
                    <td className="px-4 py-3 text-sm">Total (5 months)</td>
                    <td className="px-4 py-3 text-sm text-right">₺750,000</td>
                    <td className="px-4 py-3 text-sm text-right">₺225,000</td>
                    <td className="px-4 py-3 text-sm text-right">₺525,000</td>
                    <td className="px-4 py-3 text-sm text-right">28,250,000</td>
                    <td className="px-4 py-3 text-sm text-right">17,750,000</td>
                    <td className="px-4 py-3 text-sm text-right">111,600</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="mt-5 p-4 rounded-xl bg-amber-50 border border-amber-200">
            <div className="flex items-start gap-2.5">
              <div className="flex-shrink-0 mt-0.5">
                <svg className="w-4 h-4 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <p className="text-xs font-semibold text-amber-800">Estimated Performance Projections</p>
                <p className="text-[11px] text-amber-700 mt-1 leading-relaxed">
                  This always-on plan is built on estimated projections based on current market benchmarks for the Turkish digital advertising market.
                  Actual results may vary depending on auction dynamics, audience behavior, seasonality, creative performance, and market conditions.
                  Monthly performance may fluctuate; figures represent average expected outcomes per month.
                </p>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Wunder — ASICS Always On Campaign Plan — ₺1,100,000 */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden mt-12">
        <div className="bg-gradient-to-r from-[#001E62] via-[#00308F] to-[#001E62] p-5 text-white flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <div className="bg-white rounded-md px-2 py-1 shadow-sm">
              <img src="/wunderlogo.webp" alt="Wunder" className="h-7 w-auto object-contain" />
            </div>
            <div className="flex items-center gap-2.5">
              <div className="bg-white rounded-lg px-3 py-1.5 shadow-md flex items-center gap-2">
                <img src="/asicslogo.png" alt="ASICS" className="h-6 w-auto object-contain" />
              </div>
            </div>
            <div className="ml-1">
              <h2 className="text-lg font-bold">Always On Performance Plan</h2>
              <p className="text-sm text-blue-200">Google Ads (Search + PMAX + Demand Gen) & Meta (Sales + Reach + Traffic) • Excl. Discounted Items</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center px-3 py-1 bg-white/20 text-white text-xs font-medium rounded-full border border-white/30">
              Feb – Dec 2026
            </span>
            <span className="inline-flex items-center px-3 py-1.5 bg-emerald-500 text-white text-sm font-semibold rounded-full shadow-lg shadow-emerald-500/30">
              Total: ₺1,100,000
            </span>
          </div>
        </div>

        <div className="p-6">
          {/* Budget Split Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
              <div className="flex items-center gap-2 mb-1">
                <img src="/google_ads-512.webp" alt="Google" className="w-5 h-5 object-contain" />
                <span className="text-xs font-medium text-blue-700">Google Ads</span>
              </div>
              <p className="text-xl font-bold text-blue-800">₺440,000</p>
              <p className="text-[10px] text-blue-600 mt-0.5">40% of total • Search + PMAX + Demand Gen</p>
            </div>
            <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-4">
              <div className="flex items-center gap-2 mb-1">
                <img src="/6033716.png" alt="Meta" className="w-5 h-5 object-contain" />
                <span className="text-xs font-medium text-indigo-700">Meta Ads</span>
              </div>
              <p className="text-xl font-bold text-indigo-800">₺660,000</p>
              <p className="text-[10px] text-indigo-600 mt-0.5">60% of total • Sales + Reach + Traffic</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-medium text-gray-700">Total Clicks</span>
              </div>
              <p className="text-xl font-bold text-gray-800">185.5K</p>
              <p className="text-[10px] text-gray-600 mt-0.5">Google 75.5K + Meta 110K</p>
            </div>
            <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-4">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-medium text-emerald-700">Total Reach</span>
              </div>
              <p className="text-xl font-bold text-emerald-800">22.9M</p>
              <p className="text-[10px] text-emerald-600 mt-0.5">Across all campaigns (11 months)</p>
            </div>
          </div>

          {/* Campaign Plan Table — Full Period Totals */}
          <div className="rounded-xl border overflow-hidden mb-8">
            <div className="bg-slate-100 px-4 py-3 font-semibold text-gray-800">Campaign Plan — Full Period Totals (Feb – Dec 2026)</div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Platform</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campaign Type</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Impressions</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Reach</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Clicks</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">CPC (TRY)</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">CPM (TRY)</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Budget</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {/* Google Ads - Search */}
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium" rowSpan={3}>
                      <div className="flex items-center gap-2">
                        <img src="/google_ads-512.webp" alt="Google Ads" className="w-5 h-5 object-contain" />
                        <span>Google Ads</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">Search</td>
                    <td className="px-4 py-3 text-sm text-right">-</td>
                    <td className="px-4 py-3 text-sm text-right">-</td>
                    <td className="px-4 py-3 text-sm text-right">29,090</td>
                    <td className="px-4 py-3 text-sm text-right">₺5.5</td>
                    <td className="px-4 py-3 text-sm text-right">-</td>
                    <td className="px-4 py-3 text-sm text-right font-medium">₺160,000</td>
                  </tr>
                  {/* Google Ads - PMAX */}
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">Performance Max</td>
                    <td className="px-4 py-3 text-sm text-right">-</td>
                    <td className="px-4 py-3 text-sm text-right">-</td>
                    <td className="px-4 py-3 text-sm text-right">36,360</td>
                    <td className="px-4 py-3 text-sm text-right">₺5.5</td>
                    <td className="px-4 py-3 text-sm text-right">-</td>
                    <td className="px-4 py-3 text-sm text-right font-medium">₺200,000</td>
                  </tr>
                  {/* Google Ads - Demand Gen */}
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">Demand Gen</td>
                    <td className="px-4 py-3 text-sm text-right">4,000,000</td>
                    <td className="px-4 py-3 text-sm text-right">2,800,000</td>
                    <td className="px-4 py-3 text-sm text-right">10,000</td>
                    <td className="px-4 py-3 text-sm text-right">₺8.0</td>
                    <td className="px-4 py-3 text-sm text-right">₺20.0</td>
                    <td className="px-4 py-3 text-sm text-right font-medium">₺80,000</td>
                  </tr>
                  {/* Google Subtotal */}
                  <tr className="bg-blue-50 font-semibold">
                    <td className="px-4 py-3 text-sm text-blue-800">Google Total</td>
                    <td className="px-4 py-3 text-sm"></td>
                    <td className="px-4 py-3 text-sm text-right text-blue-800">4,000,000</td>
                    <td className="px-4 py-3 text-sm text-right text-blue-800">2,800,000</td>
                    <td className="px-4 py-3 text-sm text-right text-blue-800">75,450</td>
                    <td className="px-4 py-3 text-sm text-right text-blue-800">₺5.8</td>
                    <td className="px-4 py-3 text-sm text-right text-blue-800">-</td>
                    <td className="px-4 py-3 text-sm text-right text-blue-800">₺440,000</td>
                  </tr>
                  {/* Meta - Sales */}
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium" rowSpan={3}>
                      <div className="flex items-center gap-2">
                        <img src="/6033716.png" alt="Meta" className="w-5 h-5 object-contain" />
                        <span>Meta Ads</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">Sales (Advantage+)</td>
                    <td className="px-4 py-3 text-sm text-right">4,730,000</td>
                    <td className="px-4 py-3 text-sm text-right">2,365,000</td>
                    <td className="px-4 py-3 text-sm text-right">50,000</td>
                    <td className="px-4 py-3 text-sm text-right">₺5.2</td>
                    <td className="px-4 py-3 text-sm text-right">-</td>
                    <td className="px-4 py-3 text-sm text-right font-medium">₺260,000</td>
                  </tr>
                  {/* Meta - Reach */}
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">Reach</td>
                    <td className="px-4 py-3 text-sm text-right">18,330,000</td>
                    <td className="px-4 py-3 text-sm text-right">13,200,000</td>
                    <td className="px-4 py-3 text-sm text-right">-</td>
                    <td className="px-4 py-3 text-sm text-right">-</td>
                    <td className="px-4 py-3 text-sm text-right">₺12.0</td>
                    <td className="px-4 py-3 text-sm text-right font-medium">₺220,000</td>
                  </tr>
                  {/* Meta - Traffic */}
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">Traffic</td>
                    <td className="px-4 py-3 text-sm text-right">9,000,000</td>
                    <td className="px-4 py-3 text-sm text-right">4,500,000</td>
                    <td className="px-4 py-3 text-sm text-right">60,000</td>
                    <td className="px-4 py-3 text-sm text-right">₺3.0</td>
                    <td className="px-4 py-3 text-sm text-right">-</td>
                    <td className="px-4 py-3 text-sm text-right font-medium">₺180,000</td>
                  </tr>
                  {/* Meta Subtotal */}
                  <tr className="bg-indigo-50 font-semibold">
                    <td className="px-4 py-3 text-sm text-indigo-800">Meta Total</td>
                    <td className="px-4 py-3 text-sm"></td>
                    <td className="px-4 py-3 text-sm text-right text-indigo-800">32,060,000</td>
                    <td className="px-4 py-3 text-sm text-right text-indigo-800">20,065,000</td>
                    <td className="px-4 py-3 text-sm text-right text-indigo-800">110,000</td>
                    <td className="px-4 py-3 text-sm text-right text-indigo-800">₺4.0</td>
                    <td className="px-4 py-3 text-sm text-right text-indigo-800">₺20.6</td>
                    <td className="px-4 py-3 text-sm text-right text-indigo-800">₺660,000</td>
                  </tr>
                  {/* Grand Total */}
                  <tr className="bg-gray-800 text-white font-bold">
                    <td className="px-4 py-3 text-sm">Grand Total</td>
                    <td className="px-4 py-3 text-sm"></td>
                    <td className="px-4 py-3 text-sm text-right">36,060,000</td>
                    <td className="px-4 py-3 text-sm text-right">22,865,000</td>
                    <td className="px-4 py-3 text-sm text-right">185,450</td>
                    <td className="px-4 py-3 text-sm text-right">-</td>
                    <td className="px-4 py-3 text-sm text-right">₺30.5</td>
                    <td className="px-4 py-3 text-sm text-right">₺1,100,000</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Monthly Breakdown Table */}
          <div className="rounded-xl border overflow-hidden mb-6">
            <div className="bg-slate-100 px-4 py-3 font-semibold text-gray-800">Monthly Breakdown — Always On (Feb – Dec 2026)</div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Month</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Budget</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Google Ads</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Meta Ads</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Impressions</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Reach</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Clicks</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {/* February — ramp-up */}
                  <tr className="bg-white hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">February</td>
                    <td className="px-4 py-3 text-sm text-right font-medium">₺90,000</td>
                    <td className="px-4 py-3 text-sm text-right">₺36,000</td>
                    <td className="px-4 py-3 text-sm text-right">₺54,000</td>
                    <td className="px-4 py-3 text-sm text-right">2,800,000</td>
                    <td className="px-4 py-3 text-sm text-right">1,780,000</td>
                    <td className="px-4 py-3 text-sm text-right">14,400</td>
                  </tr>
                  {/* March */}
                  <tr className="bg-gray-50 hover:bg-gray-100">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">March</td>
                    <td className="px-4 py-3 text-sm text-right font-medium">₺95,000</td>
                    <td className="px-4 py-3 text-sm text-right">₺38,000</td>
                    <td className="px-4 py-3 text-sm text-right">₺57,000</td>
                    <td className="px-4 py-3 text-sm text-right">3,050,000</td>
                    <td className="px-4 py-3 text-sm text-right">1,940,000</td>
                    <td className="px-4 py-3 text-sm text-right">15,700</td>
                  </tr>
                  {/* April */}
                  <tr className="bg-white hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">April</td>
                    <td className="px-4 py-3 text-sm text-right font-medium">₺95,000</td>
                    <td className="px-4 py-3 text-sm text-right">₺38,000</td>
                    <td className="px-4 py-3 text-sm text-right">₺57,000</td>
                    <td className="px-4 py-3 text-sm text-right">3,180,000</td>
                    <td className="px-4 py-3 text-sm text-right">2,020,000</td>
                    <td className="px-4 py-3 text-sm text-right">16,350</td>
                  </tr>
                  {/* May */}
                  <tr className="bg-gray-50 hover:bg-gray-100">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">May</td>
                    <td className="px-4 py-3 text-sm text-right font-medium">₺95,000</td>
                    <td className="px-4 py-3 text-sm text-right">₺38,000</td>
                    <td className="px-4 py-3 text-sm text-right">₺57,000</td>
                    <td className="px-4 py-3 text-sm text-right">3,240,000</td>
                    <td className="px-4 py-3 text-sm text-right">2,060,000</td>
                    <td className="px-4 py-3 text-sm text-right">16,650</td>
                  </tr>
                  {/* June — summer, reduced */}
                  <tr className="bg-white hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">June</td>
                    <td className="px-4 py-3 text-sm text-right font-medium text-amber-700">₺80,000</td>
                    <td className="px-4 py-3 text-sm text-right">₺32,000</td>
                    <td className="px-4 py-3 text-sm text-right">₺48,000</td>
                    <td className="px-4 py-3 text-sm text-right">2,570,000</td>
                    <td className="px-4 py-3 text-sm text-right">1,630,000</td>
                    <td className="px-4 py-3 text-sm text-right">13,200</td>
                  </tr>
                  {/* July — summer low */}
                  <tr className="bg-gray-50 hover:bg-gray-100">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">July</td>
                    <td className="px-4 py-3 text-sm text-right font-medium text-amber-700">₺75,000</td>
                    <td className="px-4 py-3 text-sm text-right">₺30,000</td>
                    <td className="px-4 py-3 text-sm text-right">₺45,000</td>
                    <td className="px-4 py-3 text-sm text-right">2,340,000</td>
                    <td className="px-4 py-3 text-sm text-right">1,480,000</td>
                    <td className="px-4 py-3 text-sm text-right">12,000</td>
                  </tr>
                  {/* August — summer, slight recovery */}
                  <tr className="bg-white hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">August</td>
                    <td className="px-4 py-3 text-sm text-right font-medium text-amber-700">₺80,000</td>
                    <td className="px-4 py-3 text-sm text-right">₺32,000</td>
                    <td className="px-4 py-3 text-sm text-right">₺48,000</td>
                    <td className="px-4 py-3 text-sm text-right">2,540,000</td>
                    <td className="px-4 py-3 text-sm text-right">1,610,000</td>
                    <td className="px-4 py-3 text-sm text-right">13,100</td>
                  </tr>
                  {/* September — ramp up */}
                  <tr className="bg-gray-50 hover:bg-gray-100">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">September</td>
                    <td className="px-4 py-3 text-sm text-right font-medium">₺105,000</td>
                    <td className="px-4 py-3 text-sm text-right">₺42,000</td>
                    <td className="px-4 py-3 text-sm text-right">₺63,000</td>
                    <td className="px-4 py-3 text-sm text-right">3,550,000</td>
                    <td className="px-4 py-3 text-sm text-right">2,250,000</td>
                    <td className="px-4 py-3 text-sm text-right">18,250</td>
                  </tr>
                  {/* October — peak */}
                  <tr className="bg-emerald-50 hover:bg-emerald-100">
                    <td className="px-4 py-3 text-sm font-bold text-emerald-900">October</td>
                    <td className="px-4 py-3 text-sm text-right font-bold text-emerald-800">₺130,000</td>
                    <td className="px-4 py-3 text-sm text-right font-medium text-emerald-700">₺52,000</td>
                    <td className="px-4 py-3 text-sm text-right font-medium text-emerald-700">₺78,000</td>
                    <td className="px-4 py-3 text-sm text-right font-medium text-emerald-700">4,470,000</td>
                    <td className="px-4 py-3 text-sm text-right font-medium text-emerald-700">2,840,000</td>
                    <td className="px-4 py-3 text-sm text-right font-medium text-emerald-700">23,000</td>
                  </tr>
                  {/* November — highest */}
                  <tr className="bg-emerald-50 hover:bg-emerald-100">
                    <td className="px-4 py-3 text-sm font-bold text-emerald-900">November</td>
                    <td className="px-4 py-3 text-sm text-right font-bold text-emerald-800">₺135,000</td>
                    <td className="px-4 py-3 text-sm text-right font-medium text-emerald-700">₺54,000</td>
                    <td className="px-4 py-3 text-sm text-right font-medium text-emerald-700">₺81,000</td>
                    <td className="px-4 py-3 text-sm text-right font-medium text-emerald-700">4,690,000</td>
                    <td className="px-4 py-3 text-sm text-right font-medium text-emerald-700">2,980,000</td>
                    <td className="px-4 py-3 text-sm text-right font-medium text-emerald-700">24,100</td>
                  </tr>
                  {/* December — high season */}
                  <tr className="bg-white hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">December</td>
                    <td className="px-4 py-3 text-sm text-right font-medium">₺120,000</td>
                    <td className="px-4 py-3 text-sm text-right">₺48,000</td>
                    <td className="px-4 py-3 text-sm text-right">₺72,000</td>
                    <td className="px-4 py-3 text-sm text-right">4,010,000</td>
                    <td className="px-4 py-3 text-sm text-right">2,550,000</td>
                    <td className="px-4 py-3 text-sm text-right">20,650</td>
                  </tr>
                  {/* Total */}
                  <tr className="bg-gray-800 text-white font-bold">
                    <td className="px-4 py-3 text-sm">Total (11 months)</td>
                    <td className="px-4 py-3 text-sm text-right">₺1,100,000</td>
                    <td className="px-4 py-3 text-sm text-right">₺440,000</td>
                    <td className="px-4 py-3 text-sm text-right">₺660,000</td>
                    <td className="px-4 py-3 text-sm text-right">36,440,000</td>
                    <td className="px-4 py-3 text-sm text-right">23,140,000</td>
                    <td className="px-4 py-3 text-sm text-right">187,400</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="mt-5 p-4 rounded-xl bg-amber-50 border border-amber-200">
            <div className="flex items-start gap-2.5">
              <div className="flex-shrink-0 mt-0.5">
                <svg className="w-4 h-4 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <p className="text-xs font-semibold text-amber-800">Estimated Performance Projections</p>
                <p className="text-[11px] text-amber-700 mt-1 leading-relaxed">
                  This always-on plan excludes discounted/promotional items and is built on estimated projections based on current market benchmarks for the Turkish digital advertising market.
                  Actual results may vary depending on auction dynamics, audience behavior, seasonality, creative performance, and market conditions.
                  Budget is weighted towards Q4 (Oct–Nov peak) to align with higher purchase intent and seasonal demand.
                </p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>

    {/* --- KIXX Budget Distribution Plan — ₺50,000 --- */}
    <div className="bg-white rounded-2xl shadow-sm border overflow-hidden mt-12">
      <div className="bg-gradient-to-r from-[#121212] via-[#2b2b2b] to-[#121212] p-5 text-white flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-bold">KIXX — Budget Distribution Plan</h2>
          <p className="text-sm text-gray-200">Meta: Hızlı Form + IG DM + Conversion (Lead) | Google: Generic</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center px-3 py-1.5 bg-emerald-500 text-white text-sm font-semibold rounded-full shadow-lg shadow-emerald-500/30">
            Total: ₺50,000
          </span>
        </div>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-4">
            <span className="text-xs font-medium text-indigo-700">Meta Total</span>
            <p className="text-xl font-bold text-indigo-800">₺30,000</p>
            <p className="text-[10px] text-indigo-600 mt-0.5">60% of total budget</p>
          </div>
          <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
            <span className="text-xs font-medium text-blue-700">Google Total</span>
            <p className="text-xl font-bold text-blue-800">₺20,000</p>
            <p className="text-[10px] text-blue-600 mt-0.5">40% of total budget</p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
            <span className="text-xs font-medium text-gray-700">Meta Objectives</span>
            <p className="text-xl font-bold text-gray-800">3</p>
            <p className="text-[10px] text-gray-600 mt-0.5">Lead-gen odaklı kırılım</p>
          </div>
          <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-4">
            <span className="text-xs font-medium text-emerald-700">Google Objectives</span>
            <p className="text-xl font-bold text-emerald-800">1</p>
            <p className="text-[10px] text-emerald-600 mt-0.5">Generic kampanya</p>
          </div>
        </div>

        <div className="rounded-xl border overflow-hidden">
          <div className="bg-slate-100 px-4 py-3 font-semibold text-gray-800">Channel & Campaign Budget Breakdown</div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Channel</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campaign Type</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Budget</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Share</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium" rowSpan={3}>Meta</td>
                  <td className="px-4 py-3 text-sm">Hızlı Form</td>
                  <td className="px-4 py-3 text-sm text-right">₺12,000</td>
                  <td className="px-4 py-3 text-sm text-right">24.0%</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm">IG DM</td>
                  <td className="px-4 py-3 text-sm text-right">₺7,500</td>
                  <td className="px-4 py-3 text-sm text-right">15.0%</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm">Conversion (Lead)</td>
                  <td className="px-4 py-3 text-sm text-right">₺10,500</td>
                  <td className="px-4 py-3 text-sm text-right">21.0%</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium">Google</td>
                  <td className="px-4 py-3 text-sm">Generic</td>
                  <td className="px-4 py-3 text-sm text-right">₺20,000</td>
                  <td className="px-4 py-3 text-sm text-right">40.0%</td>
                </tr>
                <tr className="bg-gray-100 font-semibold">
                  <td className="px-4 py-3 text-sm" colSpan={2}>TOTAL</td>
                  <td className="px-4 py-3 text-sm text-right">₺50,000</td>
                  <td className="px-4 py-3 text-sm text-right">100%</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
    </>
  );
} 